import type { LoaderFunctionArgs } from '~/types/route-args';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { redirect, data as routerData } from 'react-router';
import {
    useFetchers,
    useLoaderData,
    useLocation,
    useNavigate,
    useNavigationType,
    useSearchParams,
    type ShouldRevalidateFunction,
    data,
} from 'react-router';
import invariant from 'tiny-invariant';
import type { MetaDescriptor } from 'react-router';
import {
    getCanonicalHreflang,
    createMetaTitle,
    createMetaDescription,
    fullCountryNameFromCode,
    createOGTags,
    mergeMeta,
} from '~/modules/seo';
import { createFAQSchema } from '~/modules/seo/schema/faq-page-schema';
import { createProductsItemListSchema } from '~/modules/seo/schema/item-list-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import {
    pxToRem,
    useAppDisplayModeContext,
    useTheme,
} from '@archipro-design/aria';
import { PermissionEnum } from 'generated/graphql';
import { NoResultsSection } from '@modules/category/component/no-results/NoResultsSection';
import {
    querySearchServiceAggregate,
    querySearchServiceSearch,
    type SearchResults,
    type SearchResultItem,
    type SearchAggregations,
} from '~/modules/search';

import { createESAggregationProps } from '@modules/search/util/create-es-aggregation-props';
import {
    createEmptyFilterStates,
    createFilterStates,
} from '~/modules/search/util/create-filter-states';
import { adRequest } from '@modules/adverts/api';
import AdvertBannerTile from '@modules/adverts/component/advert-banner-tile/AdvertBannerTile';
import { createPaginationState } from '@modules/pagination/util/create-pagination-state.server';
import { TitleSectionMobile } from '~/modules/category/component/title-section/TitleSection.mobile';
import { TitleSectionDesktop } from '~/modules/category/component/title-section/TitleSection.desktop';
import { getDirectoryPageMetaCombinedCached } from '~/modules/category/api/get-directory-page-meta-combined-cached.server';
import { CollapsibleToggle } from '~/modules/category/component/collapsible-page-layout/CollapsibleToggle';
import {
    USER_PREFERENCE_KEY,
    useSearchFilters,
} from '~/modules/search/hook/use-search-filters';
import type { FilterStatesKey } from '~/modules/search/type/filter-state';
import { useCategoryFilterSort } from '@ui-rocco/hooks/use-category-filter-sort';
import type { AdvertCreative } from '@modules/adverts/type';
import { useTracker } from '@archipro-website/tracker';
import { getDirectoryFullName } from '~/modules/category/util/directory-helpers';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { getSession } from '~/modules/root/util/session.server';
import TitleSectionFilterMobile from '~/modules/category/component/title-section/TitleSectionFilter.mobile';
import {
    hasFeature,
    isUsProductLaunchEnabled,
    useFeatureFlags,
    useRootData,
    type APHandle,
} from '~/modules/root';
import USProductsComingSoonPage from '~/modules/us-products-coming-soon/page/USProductsComingSoonPage';
import { shuffleArray } from '~/utils/arrayHelper';
import { decodeEntities } from '~/utils/stringHelper';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { getProductCategoriesCached as getProductCategoriesRocco } from '~/modules-rocco/products/api/get-product-categories-cached.server';
import { parseURLToESQueryParams } from '~/modules/search/util/filter-url-helpers';
import { getString, parseURL } from '@archipro-website/top-navigation';
import { parseURL as parseURLRocco } from '@modules-rocco/url/api/parse-url';
import { validateSort } from '~/modules/search/util/apply-es-search-sort';
import DetailedProductSpliter from '~/modules/products-category/component/DetailedProductSpliter';
import { mapURLToFilterStates } from '@modules-rocco/filters/data-mappers/url-to-filter-state';
import {
    PRODUCT_FILTERS_CONFIG,
    PRODUCT_FILTERS_CONFIG_REVAMP,
} from '@modules-rocco/products/config/filter.config';
import { setFilterStatesAggregations } from '@modules-rocco/filters/api/create-filter-states';
import { SearchIndexes } from '@modules-rocco/search/types/enum/search-indexes';
import { mapCategoriesToDropdownGroups } from '~/modules-rocco/categories/data-mappers/category-to-dropdown-groups';
import { getSearchAggregationsCached } from '@modules-rocco/search/api/get-search-aggregations-cached.server';
import ArchizeenAdvertTile from '~/modules/tile/component/archizeen-tile/ArchizeenAdvertTile';
import { getArticleTileViewModelFromAd } from '~/modules/adverts/utils/map-ad-data-to-tile.server';
import type { ArticleTileViewModel } from '~/modules/article';
import { getRouteData } from '~/modules/root/util/get-route-data';
import { noIndexProductCategory } from '~/modules/seo/util/no-index-product-category';
import { buildProductFilterPathRedirect } from '~/modules/products-category/util/filter-path-redirect';
import { rewriteProductFilterPathToQuery } from '~/modules/products-category/util/seo-filter-path';
import { getAppliedFilterNumber } from '~/modules/search/util/filter-state-util';
import { getPermissions } from '~/modules/user';
import {
    convertToProductSuppliersUrl,
    isProductSuppliersPage,
} from '~/modules/url';
import findLastIndex from 'lodash.findlastindex';
import BackToTopButton from '@modules/directory/component/BackToTopButton';
import { getProductsFromESSearchResults } from '@modules-rocco/search/data-mappers/tile-product';
import type { ProductTileViewModel as ProductTileViewModelRocco } from '@rocco/ui/tile-product/view-models/product';
import type {
    TileViewModel,
    TileableModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import { Grid } from '@rocco/ui/layout/components/Grid';
import { mapCategoryCountToPlaceholder } from '~/modules-rocco/categories/data-mappers/category-count-to-placeholder';
import { getActiveDirectoryCategoriesCountCached } from '~/modules-rocco/categories/api/get-active-directory-categories-count-cached.server';
import { RecentlyViewedWrapper } from '~/ui-rocco/pages/products-page/RecentlyViewedWrapper';
import { useCategoryTilesTracking } from '@ui-rocco/hooks/use-category-tiles-tracking';
import { Pagination as TilePagination } from '@rocco/ui/pagination/components/Pagination';
import { CategoryPageFAQ } from '~/ui-rocco/pages/category-page/CategoryPageFAQ';
import { parseFooterContentToFAQ } from '~/modules/professionals-category/util/parse-footer-content-to-faq.server';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { getProfessionalsFromESSearchResults } from '~/modules-rocco/search/data-mappers/tile-professional';
import { BrowseByBrand } from '~/ui-rocco/pages/products-page/BrowseByBrand';
import { FeaturedBanner } from '~/ui-rocco/pages/products-page/FeaturedBanner';
import { extractCategoryPath } from '~/modules-rocco/featured-banner/util/extract-category-path';
import { EditorsChoice } from '~/ui-rocco/pages/products-page/EditorsChoice';
import { EditorsEdit } from '~/ui-rocco/pages/products-page/EditorsEdit';
import { ExploreAllCategories } from '~/ui-rocco/pages/products-page/ExploreAllCategories';
import { ProductsFilterBar } from '~/ui-rocco/pages/products-page/ProductsFilterBar';
import { DirectoryBrowsePage } from '~/ui-rocco/pages/directory-browse/DirectoryBrowsePage';
import { DirectoryPillBar } from '~/ui-rocco/pages/directory-browse/DirectoryPillBar';
import { DirectoryFilterSidebar } from '~/ui-rocco/pages/directory-browse/DirectoryFilterSidebar';
import { DirectoryIntro } from '~/ui-rocco/pages/directory-browse/DirectoryIntro';
import { GetMatchedModal } from '~/ui-rocco/pages/category-page/GetMatchedModal';
import { useCategoryRevamp } from '@modules/category/hook/use-category-revamp';
import { isCategoryRevampActive } from '@modules/category/util/is-category-revamp-active.server';
import { DIRECTORY_DEFAULT_SORT } from '@modules/category/util/directory-default-sort';
import {
    hasEnoughPinnedSuppliers,
    interleaveByProfessional,
    isDefaultProductListActive,
    pickDefaultProductListBlock,
    MIN_BLOCK_HITS,
} from '@modules/category/util/default-product-list.server';
import { getFeaturedBrandPinsCached } from '@modules-rocco/professionals/api/get-featured-brand-pins.server';
import { CheckboxBlankLine, CheckboxFill } from '@archipro-design/icons';
import { buildPaginationUrl } from '@modules-rocco/pagination/util/build-pagination-url';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { FollowSelectorProxy } from '~/modules/moodboards/components';
import { VirtualizedGrid } from '~/components/VirtualizedGrid';
import { cn } from '@rocco/utils/cn';
import { useDeviceBrowserContext } from '@archipro-design/aria';
import { getItemToAmplify } from '~/modules/professional/api/get-item-to-amplify.server';
import { AnalyticsAmplifierTile } from '@rocco/ui/tile-analytics-amplifier/components/AnlyticsAmplifier';
import { useABTestIsOn } from '~/modules/root/hook/use-growthbook';
import { getHiddenFacets } from '~/modules/product/api/get-facets.server';
import { raceAbort, isAbortError } from '~/utils/abort-utils';

import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { CategoryPageProducts } from '~/ui-rocco/pages/category-page/CategoryPageProducts';
import {
    applyCategoryFallbackImages,
    buildCategoryCountMap,
    mapCategoriesToTileViewModel,
} from '@modules-rocco/categories/data-mappers/category-to-tile';
import { getCategoryFallbackImagesCached } from '@modules-rocco/categories/api/get-category-fallback-images-cached.server';
import { getSupplierCountsByCategory } from '@modules-rocco/products/api/get-supplier-counts-by-category.server';
import { useProductPageTracking } from '@ui-rocco/hooks/use-product-page-tracking';
import { getDirectoryFeaturedItemsCached } from '@modules-rocco/featured-item/api/get-directory-featured-items-cached.server';
import { FeaturedItemContent } from 'generated/graphql';
import { DirectoryProductGrid } from '~/ui-rocco/pages/category-page/DirectoryProductGrid';
import {
    DirectoryUniformGrid,
    type DirectoryGridColumns,
} from '~/ui-rocco/pages/category-page/DirectoryUniformGrid';
import { useDirectoryDisplayColumns } from '~/ui-rocco/pages/directory-browse/directory-display-columns';
import { useDirectoryGridInjections } from '~/ui-rocco/pages/directory-browse/use-directory-grid-injections';
import { useLibraryRemovalUi } from '~/modules/directory/hook/use-library-removal-ui';
import {
    LibraryActionProvider,
    useLibraryAction,
} from '~/modules/directory/context/LibraryActionContext';
import { DirectoryAdTile } from '~/ui-rocco/pages/category-page/DirectoryAdTile';
import {
    useProductViewMore,
    useDirectoryViewMore,
    getProductCountForPage,
    getMaxPage,
    PRODUCTS_PER_PAGE,
    BROWSE_PRODUCTS_PER_PAGE,
} from '~/ui-rocco/pages/category-page/hooks/useProductViewMore';
import { useIsomorphicLayoutEffect } from 'ahooks';
import { TileViewTransitionScope } from '@rocco/contexts/TileViewTransitionScopeContext';
import {
    findFirstVisibleGridTile,
    pollForTileAndScroll,
} from '~/ui-rocco/pages/category-page/utils/findGridTile';

// In-memory scroll position store — works in all browsing modes including private.
// Positions persist across navigations within the same tab session.
// Stores scrollY + the product link of the tile the user was looking at,
// so POP restoration can wait for that tile to appear in the DOM.
const scrollPositions = new Map<
    string,
    { scrollY: number; tileLink: string | null }
>();

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches, location } = props;

        // Coming Soon loader returns a minimal payload — keep the page out of
        // the index while the products section is not yet launched.
        if (data && 'usComingSoon' in data) {
            return [
                { title: 'Products — Coming Soon | ArchiPro' },
                {
                    name: 'description',
                    content:
                        'ArchiPro Products is the catalogue America has been missing — every brand, every category, every spec sheet, all in one place. Not yet open.',
                },
                { name: 'robots', content: 'noindex, follow' },
            ];
        }

        const { rootData } = getRouteData(matches);

        const title = createMetaTitle(
            props,
            [
                data?.localisation?.Title,
                data?.directoryPageMeta?.category
                    ? `Buy ${data?.directoryPageMeta?.category} Online`
                    : 'Page Not Found',
            ],
            true,
            data?.pagination?.page,
        );

        invariant(
            rootData?.localisation.country,
            'rootData is required for product category SEO',
        );

        const description = createMetaDescription([
            data?.localisation?.Description,
            ` Browse a wide range of ${
                data?.directoryPageMeta?.category
            } on ArchiPro. We offer high-quality products from leading brands in ${fullCountryNameFromCode(
                rootData.localisation.country,
            )}. Shop now!`,
        ]);

        const productsListSchema = createProductsItemListSchema(props);
        const FAQSchema = createFAQSchema(data?.localisation?.FAQSchemas ?? []);
        const ldSchema = createLDSchema([productsListSchema ?? [], FAQSchema]);

        // Preloading disabled - eager loading with fetchPriority="high" provides similar performance benefits
        const preloadImageLinks: MetaDescriptor[] = [];

        const metaDescriptors = [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
                type: 'website',
            }),
            ...getCanonicalHreflang(props),
            ...ldSchema,
            ...preloadImageLinks,
        ];

        return noIndexProductCategory(
            metaDescriptors,
            location.pathname + location.search,
            // Backend per-combo indexability verdict (unique SEO content + enough supply),
            // shared with the sitemap so robots and sitemap can never disagree.
            data?.localisation?.IsIndexableFilterPage,
        );
    },
);

export const handle: APHandle = {
    isCategoryPage: true,
    hideContactArchiPro: false,
    disableScrollRestoration: true,
    // Big-A unified header (BC-4480) — matches the approved directory mock.
    header: { showLogomark: true },
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
    defaultShouldRevalidate,
    formAction,
}) => {
    // Do not revalidate loader on form actions. Only revalidate on URL and
    // URL search param changes.
    if (formAction) {
        return false;
    }
    return defaultShouldRevalidate;
};

const CATEGORY_IGNORE_FILTER_KEYS: FilterStatesKey[] = [
    'libraryRanges',
    'awards',
];

// Product page size is defined in useProductViewMore.ts as PRODUCTS_PER_PAGE (42)
// 3 complete sections (14 products each) for A/B/A layout
const DEFAULT_PRODUCT_PAGE_SIZE = 40;
// Matches BROWSE_PRODUCTS_PER_PAGE, and for the same reason: 36 divides evenly
// by 2, 3, 4 and 6, so a page ends on a full row at every column count the grid
// offers. The old 39 was odd — it orphaned a lone tile on the 2-up mobile grid
// and ran ragged at the desktop default of 4.
const DEFAULT_SUPPLIERS_PAGE_SIZE = 36;
const DEFAULT_SORT = DIRECTORY_DEFAULT_SORT.products;
export const PRODUCT_TILE_EXPANDED_WIDTH = 368;
export const PRODUCT_TILE_COLLAPSED_WIDTH = 340;
export const PRODUCT_TILE_PAGING_WIDTH = 175;
export const PRODUCT_TILE_BULLET_SIZE = 11;

// Build a new products path toggling the suppliers segment while keeping the remaining category segments.
const buildTogglePath = (pathname: string, toSuppliers: boolean) => {
    const segments = pathname.split('/').filter(Boolean);
    if (segments[0] !== 'products') {
        return toSuppliers ? '/products/suppliers' : '/products';
    }

    if (toSuppliers) {
        if (segments[1] === 'suppliers') {
            return `/${segments.join('/')}`;
        }

        return `/${['products', 'suppliers', ...segments.slice(1)].join('/')}`;
    }

    if (segments[1] === 'suppliers') {
        const rest = segments.slice(2);
        return `/${['products', ...rest].join('/')}`;
    }

    return `/${segments.join('/')}`;
};

// Build the toggle URL by adding the browse flag and using the updated products path.
// Clears all filter params — products and suppliers have different filter sets.
const buildToggleUrl = (
    pathname: string,
    _search: string,
    toSuppliers: boolean,
) => {
    const path = buildTogglePath(pathname, toSuppliers);
    return `${path}?browse=true`;
};

const productsDirectoryLoader = async (args: LoaderFunctionArgs) => {
    const { context, request } = args;
    const signal = request.signal;
    // The carousel browse revamp always renders the browse grid (never landing).
    const categoryRevampActive = isCategoryRevampActive(context, request);

    // Early abort: skip all work if the request is already cancelled
    if (signal.aborted) {
        throw new Response(null, { status: 499 });
    }

    try {
        const { appDisplayMode, user, featureFlags } = context;

        const originalUrl = new URL(request.url);
        const isSuppliersEndpoint = isProductSuppliersPage(
            originalUrl.pathname,
        );
        const url = isSuppliersEndpoint
            ? new URL(request.url.replace('/suppliers', ''))
            : originalUrl;
        const searchParams = url.searchParams;

        // On the bare products root the grid is restricted to the admin-pinned
        // supplier list and reordered, so the first impression is a curated mix
        // rather than whatever the deterministic Featured sort returns. Started
        // here so the (memory-cached) pins read overlaps the session read.
        const defaultProductListGateOpen = isDefaultProductListActive({
            url: originalUrl,
            isSuppliersEndpoint,
            revampActive: categoryRevampActive,
        });
        const featuredBrandPinsPromise = defaultProductListGateOpen
            ? getFeaturedBrandPinsCached(context)
            : undefined;

        const session = await getSession(request);
        const isDesktop = appDisplayMode === 'desktop';

        const { basePath, params } = parseURL(url.pathname + url.search);
        const urlSort = validateSort(getString(params, 'sort'));

        // Deep-linking support: calculate which pages to load based on URL page param
        const urlPageParam = parseInt(searchParams.get('page') ?? '1', 10);
        // Enforce a maximum page limit to prevent DoS attacks
        // Max 500 pages = 42*500 = 21,000 products, which is reasonable
        const MAX_PAGE_LIMIT = 500;
        const requestedPage = Math.min(
            Math.max(
                Number.isFinite(urlPageParam) && urlPageParam > 0
                    ? urlPageParam
                    : 1,
                1,
            ),
            MAX_PAGE_LIMIT,
        );

        // For deep-linking to page N (N >= 4):
        // - Load pages (N-2), (N-1), N (current + 2 preceding pages)
        // - Pages 1 through (N-3) will be rendered as skeletons
        // For pages 1-3: load all pages from 1 (no skeletons needed)
        const firstLoadedPage = requestedPage >= 4 ? requestedPage - 2 : 1;

        // The revamp uniform grid pages in 36s (a full 36-cell page, some of
        // whose cells go to amplifier tiles); the legacy A/B/A grid keeps 42.
        // All the deep-link offset maths below is driven off this so `?page=N`
        // stays consistent with the grid's own View More steps.
        const productsPerPage = categoryRevampActive
            ? BROWSE_PRODUCTS_PER_PAGE
            : PRODUCTS_PER_PAGE;

        // Calculate products to load for deep-linking
        // If firstLoadedPage > 1, we need to load from that page's offset
        const deepLinkOffset =
            firstLoadedPage > 1
                ? getProductCountForPage(firstLoadedPage - 1, productsPerPage)
                : 0;

        // Products to load: from firstLoadedPage through requestedPage
        const deepLinkProductCount =
            getProductCountForPage(requestedPage, productsPerPage) -
            deepLinkOffset;

        // Colour/material filters live in the path canonically (`/color-{slug}`) but the
        // Rocco URL system only understands them as query params. Rewrite those segments
        // back to query form so Rocco can both resolve the category (otherwise the bogus
        // path segment 404s the page) and reflect the active filter in the pills.
        const roccoUrl = rewriteProductFilterPathToQuery(url);

        // Create Rocco filter states from URL early so we can fetch aggregations in parallel
        const urlInfo = parseURLRocco(
            roccoUrl.pathname + roccoUrl.search,
            featureFlags,
        );
        const urlFilterStates = mapURLToFilterStates(
            PRODUCT_FILTERS_CONFIG,
            urlInfo,
            'directory',
        );

        // --- Phase 5: Sync extraction from queryES (no ES calls needed) ---
        // Extract queryParams + aggregationProps construction and redirects so they're
        // available immediately without awaiting ES calls.
        // The revamp browse grid uses the deep-link offset system (skeleton
        // pages + a later slice) on every breakpoint, so mobile must join it too
        // — otherwise a shared `?page=N` URL under-fetches and shows the wrong
        // products. The legacy mobile grid keeps standard page-based pagination.
        const usesDeepLinkPaging =
            (isDesktop || categoryRevampActive) && !isSuppliersEndpoint;

        const esSearchParams = new URLSearchParams(searchParams);
        // Strip the page param for the deep-link offset system; standard
        // page-based pagination (legacy mobile) keeps it.
        if (usesDeepLinkPaging) {
            esSearchParams.delete('page');
        }

        const isDeepLink = requestedPage > 1 && usesDeepLinkPaging;
        const esOffset = isDeepLink ? deepLinkOffset : 0;
        const hasOffset = esOffset > 0;

        // With uniform 42-product pages, both offset and count are multiples of 42.
        // For deep links, we fetch from page 0 with a size covering offset + count,
        // then slice in post-processing. This is a one-time cost on page load.
        const esPageSize = isSuppliersEndpoint
            ? DEFAULT_SUPPLIERS_PAGE_SIZE
            : isDeepLink
              ? esOffset + deepLinkProductCount
              : // The revamp browse pages in uniform productsPerPage steps via
                // useProductViewMore on every breakpoint, so it must hydrate that
                // many on mobile too — otherwise the first mobile View More offset
                // skips the tiles between the hydrated count and the page size.
                isDesktop || categoryRevampActive
                ? productsPerPage
                : DEFAULT_PRODUCT_PAGE_SIZE;

        const { queryParams } = parseURLToESQueryParams(
            `${url.pathname}?${esSearchParams.toString()}`,
            'product',
            'directory',
            { pageSize: esPageSize },
            DEFAULT_SORT,
        );

        if (hasOffset) {
            // page=0 ensures from=0; post-processing slices to the correct range
            queryParams.page = 0;
        }

        // Resolve the curated supplier list before the ES query is shaped: the
        // entry block below is only meaningful when the curation actually
        // applies, and too few pinned suppliers means we serve the full
        // catalogue from the top instead.
        const pinnedSuppliers = (await featuredBrandPinsPromise)?.map(
            (brand) => brand.urlSegment,
        );
        const defaultProductListActive =
            !!pinnedSuppliers && hasEnoughPinnedSuppliers(pinnedSuppliers);

        // Enter the curated pool at a random page-sized block so repeat visits
        // see different products. Deep links keep block 0 so a shared or
        // crawled `?page=N` resolves to the same products every time.
        const defaultProductListBlock =
            defaultProductListActive && requestedPage === 1
                ? pickDefaultProductListBlock()
                : 0;

        if (defaultProductListBlock > 0) {
            queryParams.page = defaultProductListBlock;
        }

        // Handle user preference redirect (extracted from queryES)
        const updateUserPreference =
            searchParams.get(USER_PREFERENCE_KEY) === '1';

        if (updateUserPreference) {
            session.setUserPreferences({
                forSale: queryParams.filters?.isPurchasable ?? false,
            });
            searchParams.delete(USER_PREFERENCE_KEY);
            const redirectUrl = `${url.pathname}?${searchParams.toString()}`;
            throw redirect(
                isSuppliersEndpoint
                    ? convertToProductSuppliersUrl(redirectUrl)
                    : redirectUrl,
                {
                    headers: await session.getHeaders(),
                },
            );
        }

        const aggregationProps = createESAggregationProps({
            ...queryParams,
            searchParams,
        });

        // Colour/material filters canonically live in the path (`/color-{slug}`), where the
        // combo page can be indexed and sitemapped. Permanently redirect the legacy
        // single-value query form so each combo page has exactly one URL.
        if (!isSuppliersEndpoint) {
            const filterPathRedirect = buildProductFilterPathRedirect(url);
            if (filterPathRedirect) {
                throw redirect(filterPathRedirect, 301);
            }
        }

        // Set when the random entry block turned out to be past the end of the
        // curated pool, so View More resumes from the top instead.
        let blockFallbackApplied = false;

        const queryES = async () => {
            // Clone queryParams to avoid mutating shared state (suppliers path sets searchType)
            let esQueryParams = { ...queryParams };
            let searchFilters = esQueryParams.filters;

            if (isSuppliersEndpoint) {
                // To get product suppliers:
                // 1. Get all the professionals by aggregating products by professionals.
                // 2. Search professionals filtered by the professionals from the first step.
                const professionalAggregations =
                    await querySearchServiceAggregate({
                        context,
                        queryParams: {
                            ...aggregationProps,
                            // This pass answers "which brands have a product in
                            // this category", so the reader's keyword is kept
                            // out of it — it belongs to the brand search below.
                            // Narrowing both would return only the brands that
                            // make a matching product AND carry a matching
                            // name, which is empty for most terms.
                            search: '',
                            aggregations: {
                                aggProfessionals: {
                                    search: '',
                                    size: 1000,
                                },
                            },
                        },
                        signal,
                    });
                if (professionalAggregations?.professionals) {
                    const professionals =
                        professionalAggregations.professionals;
                    esQueryParams.searchType = 'professional';
                    searchFilters = {
                        professionals: professionals.items.map(
                            (i) => i.URLSegment,
                        ),
                    };
                }
            }

            // The curated supplier list narrows the grid query only. It is
            // deliberately kept out of `queryParams.filters`, which feeds
            // `aggregationProps` and `createFilterStates` — so facet counts stay
            // unnarrowed, no filter chip appears, and the URL stays bare.
            if (defaultProductListActive && pinnedSuppliers) {
                searchFilters = {
                    ...searchFilters,
                    professionals: pinnedSuppliers,
                };
            }

            let [searchResult, aggregations] = await Promise.all([
                querySearchServiceSearch({
                    context,
                    queryParams: {
                        ...esQueryParams,
                        filters: searchFilters,
                    },
                    signal,
                }),
                querySearchServiceAggregate({
                    context,
                    queryParams: aggregationProps,
                    signal,
                }),
            ]);

            // A short block means the curated pool is shallower than the block
            // range assumes, so fall back to the first block rather than render
            // a thin grid.
            if (
                defaultProductListBlock > 0 &&
                (searchResult?.hits?.length ?? 0) < MIN_BLOCK_HITS
            ) {
                searchResult = await querySearchServiceSearch({
                    context,
                    queryParams: {
                        ...esQueryParams,
                        page: 0,
                        filters: searchFilters,
                    },
                    signal,
                });
                blockFallbackApplied = true;
            }

            return { searchResult, aggregations };
        };

        // Early browse mode detection: skip landing-mode-only data fetches when
        // we know for certain the page will be in browse mode (saves ~300-400ms).
        // Note: !shouldSkipLandingData does NOT mean it's landing mode — that still
        // depends on hasChildCategories which is determined after aggregations.
        // For leaf categories (no children), landing data is fetched but unused.
        // This is an intentional tradeoff: fetching in parallel with aggregations
        // avoids adding latency to parent category pages (the common case), and the
        // cache layer (LRU + Redis) keeps leaf-category waste minimal (~5-10ms).
        const shouldSkipLandingData =
            isSuppliersEndpoint ||
            searchParams.has('browse') ||
            !isDesktop ||
            categoryRevampActive;

        // Start productCategoriesPromise early so we can chain landing data off it.
        // Use the filter-normalised path so colour/material segments don't leak into the
        // category urlSegment (which would resolve to NotFoundError and 404 the page).
        const productCategoriesUrlInfo = parseURLRocco(roccoUrl.pathname);
        const productCategoriesPromise = getProductCategoriesRocco(
            {
                urlInfo: productCategoriesUrlInfo,
            },
            context,
        );

        // Chain fallback image fetch off productCategoriesPromise so it starts
        // as soon as categories are available and overlaps with aggregations/featured items.
        // Only fetches in landing mode candidates (desktop, not browse, not suppliers).
        const fallbackImagesPromise = shouldSkipLandingData
            ? null
            : productCategoriesPromise
                  .then((result) => {
                      const edges = result?.Categories?.edges ?? [];
                      // Collect imageless links from both top-level subcategories
                      // and grandchildren (e.g. Building category's children)
                      const imagelessLinks: string[] = [];
                      for (const edge of edges) {
                          if (!edge.node.Image?.Desktop) {
                              imagelessLinks.push(edge.node.Link);
                          }
                          // Include grandchild links (used by buildingCategories)
                          for (const child of edge.node.Categories?.edges ??
                              []) {
                              if (!child.node.Image?.Desktop) {
                                  imagelessLinks.push(child.node.Link);
                              }
                          }
                      }
                      if (!imagelessLinks.length) return null;
                      return getCategoryFallbackImagesCached(
                          productCategoriesUrlInfo.basePath,
                          imagelessLinks,
                          context,
                          signal,
                      );
                  })
                  .catch(() => null);

        // Fetch root-level categories for ExploreAllCategories (below-grid
        // "Explore all product categories"). Always shows ALL top-level
        // categories regardless of the current URL. The revamp browse now
        // renders this section below the grid, so it needs the data too — even
        // though it otherwise skips landing-mode fetches. (Cached, so a hit is
        // cheap.) Still skipped for suppliers/mobile legacy browse.
        const needsRootCategories =
            categoryRevampActive || !shouldSkipLandingData;
        const rootProductCategoriesPromise = needsRootCategories
            ? getProductCategoriesRocco(
                  {
                      urlInfo: parseURLRocco('/products'),
                  },
                  context,
              )
            : null;

        // Browse-only data: ads, promotedAds, amplifiedItems are only used in browse/grid mode.
        // Start them eagerly when we know it's browse mode.
        const browseAdsPromise = shouldSkipLandingData
            ? adRequest({
                  type: 'CategoryPlacement',
                  requestUrl: request.url,
                  addPathContext: ['region'],
                  ...context,
                  request,
              })
            : null;
        const browsePromotedAdsPromise = shouldSkipLandingData
            ? adRequest({
                  type: 'PromotedDirectoryPlacement',
                  requestUrl: request.url,
                  addPathContext: ['region'],
                  ...context,
                  request,
              })
            : null;
        const browseAmplifiedItemsPromise = shouldSkipLandingData
            ? getItemToAmplify({
                  context,
                  link: basePath,
                  limit: isDesktop ? 5 : 4,
              })
            : null;
        let [
            { directoryPageMeta, localisation },
            activeDirectoryCategoriesCount,
            featuredPlacementResult,
            productCategoriesResult,
            roccoAggregations,
            rootProductCategoriesResult,
        ] = await raceAbort(
            signal,
            Promise.all([
                // Combined: single GraphQL call replaces separate getDirectoryPageMeta + getPageLocalisation
                // (both hit the same resolver with identical variables)
                getDirectoryPageMetaCombinedCached(args),
                getActiveDirectoryCategoriesCountCached(context),
                // Featured items are needed in both landing mode (full-width banner)
                // and browse mode (in-grid banner). Only skip for suppliers/mobile.
                isSuppliersEndpoint || !isDesktop
                    ? null
                    : getDirectoryFeaturedItemsCached(
                          {
                              content: FeaturedItemContent.Product,
                              // Pass category path for per-category banner filtering
                              targetPath: extractCategoryPath(url),
                          },
                          context,
                      ),
                productCategoriesPromise, // Reuse already-started promise
                getSearchAggregationsCached(
                    context,
                    SearchIndexes.Products,
                    urlFilterStates,
                ),
                rootProductCategoriesPromise, // Root categories for ExploreAllCategories
            ]),
        );

        // Phase 5: queryParams and aggregationProps already extracted above (sync, before Promise.all)

        const hasMarketingView = getPermissions(
            user,
            PermissionEnum.BmMarketingView,
        );
        const facetFilterFeatureEnabled = hasFeature(
            featureFlags,
            'filter_facet',
        );

        // --- Phase 5: Determine isLandingMode BEFORE awaiting ES results ---
        // Use Rocco-only data (aggregations=null → Rocco facets fallback) to determine
        // hasChildCategories. Category hierarchy is independent of legacy facetOptions.
        const preliminaryRoccoFilterStates = roccoAggregations
            ? setFilterStatesAggregations({
                  searchIndex: SearchIndexes.Products,
                  aggregations: {
                      ...roccoAggregations,
                      // Use Rocco facets only (no legacy aggregations yet)
                      facetOptions: roccoAggregations.facetOptions,
                  },
                  defaultSort: DEFAULT_SORT,
                  filterStates: urlFilterStates,
                  facetFilterFeatureEnabled,
                  hasMarketingView,
                  // hiddenFacetIds deferred: not needed for hasChildCategories determination
              })
            : urlFilterStates;

        // Create category dropdown groups for the filter pills
        const preliminaryCategoryDropdownGroups = mapCategoriesToDropdownGroups(
            preliminaryRoccoFilterStates,
        );

        // Determine if current category has child subcategories.
        // Used to show featured content for non-leaf categories, directory view for leaf categories.
        const hasChildCategories =
            (preliminaryCategoryDropdownGroups?.currentLevel?.children
                ?.length ?? 0) > 0;

        // Now that we know the page mode, conditionally await browse-only data.
        // In landing mode (hasChildCategories && not browse && not suppliers), skip entirely.
        const isLandingMode =
            hasChildCategories &&
            !searchParams.has('browse') &&
            !isSuppliersEndpoint &&
            isDesktop &&
            !categoryRevampActive;

        // Await fallback images only in landing mode (already started in parallel earlier).
        // In browse/mobile/suppliers mode, fallbackImagesPromise is null.
        const fallbackImages = isLandingMode
            ? await fallbackImagesPromise
            : null;

        // Abort check between major phases
        if (signal.aborted) {
            throw new Response(null, { status: 499 });
        }

        // --- Phase 5: Conditionally run ES and hidden facets ---
        // In landing mode, avoid starting expensive cancellable requests.
        let searchResult: SearchResults<SearchResultItem> | null = null;
        let aggregations: SearchAggregations | null = null;
        let hiddenFacets:
            | Awaited<ReturnType<typeof getHiddenFacets>>
            | undefined;

        if (!isLandingMode) {
            // Browse/leaf/suppliers navigation mode: run ES + hidden facets in parallel.
            const [esResults, hiddenFacetsResult] = await raceAbort(
                signal,
                Promise.all([queryES(), getHiddenFacets({ context, user })]),
            );
            searchResult = esResults.searchResult;
            aggregations = esResults.aggregations;
            hiddenFacets = hiddenFacetsResult;
        }

        // Clean up deep-link page param in landing mode (no product grid to paginate)
        if (isLandingMode && requestedPage > 1) {
            const cleanParams = new URLSearchParams(searchParams);
            cleanParams.delete('page');
            const cleanSearch = cleanParams.toString();
            throw redirect(
                cleanSearch ? `${url.pathname}?${cleanSearch}` : url.pathname,
            );
        }

        // --- Recompute roccoFilterStates with legacy aggregations if available ---
        // In browse mode, prefer legacy facetOptions for better filter accuracy.
        // In landing mode, the preliminary (Rocco-only) version is sufficient.
        // Use legacy aggregations.facetOptions since it correctly returns category-specific facets
        const roccoFilterStates =
            aggregations && roccoAggregations
                ? setFilterStatesAggregations({
                      searchIndex: SearchIndexes.Products,
                      aggregations: {
                          ...roccoAggregations,
                          // BC-3714: Fixed to check for non-empty object (not just null/undefined)
                          facetOptions: (() => {
                              const legacyFacets = aggregations?.facetOptions;
                              const roccoFacets =
                                  roccoAggregations.facetOptions;

                              // Prefer legacy facets if they have data
                              if (
                                  legacyFacets &&
                                  Object.keys(legacyFacets).length > 0
                              ) {
                                  return legacyFacets;
                              }

                              // Fallback to rocco facets
                              return roccoFacets;
                          })(),
                      },
                      defaultSort: DEFAULT_SORT,
                      filterStates: urlFilterStates,
                      facetFilterFeatureEnabled,
                      hasMarketingView,
                      hiddenFacetIds: hiddenFacets?.map((facet) => facet.ID),
                  })
                : preliminaryRoccoFilterStates;

        // The suppliers endpoint counts brands, not products, so the count the
        // filter UI reports has to follow the grid. Mirrors the same override
        // already applied to the legacy filterStates below.
        if (isSuppliersEndpoint) {
            roccoFilterStates.totalCount = searchResult?.nbHits ?? 0;
        }

        // Recompute category dropdown groups with final roccoFilterStates
        const categoryFiltersDropdownGroups =
            aggregations && roccoFilterStates !== preliminaryRoccoFilterStates
                ? mapCategoriesToDropdownGroups(roccoFilterStates)
                : preliminaryCategoryDropdownGroups;

        // Category cards for the carousel browse revamp's "explore categories" row.
        // At the browse root (where the carousel is shown), populate a per-category
        // distinct-supplier count so the cards render accurate "N Suppliers"
        // subtitles — the product ItemsCount is a product count, not a supplier
        // count, so it can't be used here.
        const directoryCategoryEdges = categoryRevampActive
            ? (productCategoriesResult?.Categories?.edges ?? [])
            : [];
        // Leaf categories have no children to pill. Fetch the parent's
        // children instead so mobile can switch across the sibling set (the
        // cache is usually warm — the user navigated in through the parent).
        // Desktop keeps its empty-pills presentation; the pill bar scopes
        // sibling-sourced pills to below md.
        //
        // Applies to the suppliers view too: it renders the same pill bar, and
        // the category tree is the product tree either way. The pills are
        // rewritten to /products/suppliers paths where they are built.
        const needsSiblingPills =
            categoryRevampActive &&
            directoryCategoryEdges.length === 0 &&
            (directoryPageMeta?.breadcrumbs?.length ?? 0) > 1;
        const siblingCategoryEdges = needsSiblingPills
            ? ((
                  await raceAbort(
                      signal,
                      getProductCategoriesRocco(
                          {
                              urlInfo: parseURLRocco(
                                  roccoUrl.pathname.replace(/\/[^/]+\/?$/, ''),
                              ),
                          },
                          context,
                      ),
                  )
              )?.Categories?.edges ?? [])
            : [];
        const supplierCountMap =
            directoryCategoryEdges.length > 0 && directoryPageMeta?.isRoot
                ? await getSupplierCountsByCategory(
                      directoryCategoryEdges.map((edge) => edge.node.Link),
                      context,
                      signal,
                  )
                : undefined;
        const directoryCategoryCards = mapCategoriesToTileViewModel(
            directoryCategoryEdges.length > 0
                ? directoryCategoryEdges
                : siblingCategoryEdges,
            supplierCountMap,
        );
        // Deep-linking: We fetched from page 0, so slice to get the correct range.
        // Skip the first `deepLinkOffset` products to start at the requested offset,
        // then take `deepLinkProductCount` products.
        if (
            deepLinkOffset > 0 &&
            !isSuppliersEndpoint &&
            searchResult?.hits &&
            searchResult.hits.length > deepLinkOffset
        ) {
            searchResult = {
                ...searchResult,
                hits: searchResult.hits.slice(
                    deepLinkOffset,
                    deepLinkOffset + deepLinkProductCount,
                ),
            };
        }

        // Reorder the curated hits so no supplier or category clusters. Runs on
        // the raw hits, after any deep-link slice, so the grid receives the
        // final order and no downstream mapping has to know about it.
        if (defaultProductListActive && searchResult?.hits) {
            searchResult = {
                ...searchResult,
                hits: interleaveByProfessional(searchResult.hits),
            };
        }

        const pagination = createPaginationState({
            request,
            totalCount: searchResult?.nbHits ?? 0,
            maxDisplayCount: searchResult?.maxResultWindow,
            defaultPageSize: isSuppliersEndpoint
                ? DEFAULT_SUPPLIERS_PAGE_SIZE
                : isDesktop || categoryRevampActive
                  ? productsPerPage
                  : DEFAULT_PRODUCT_PAGE_SIZE,
        });

        // Validate deep-link page number: redirect to last valid page if requested page is out of range
        const totalCount = searchResult?.nbHits ?? 0;
        if (!isSuppliersEndpoint && requestedPage > 1 && totalCount > 0) {
            const maxPage = getMaxPage(totalCount, productsPerPage);
            if (requestedPage > maxPage) {
                // Redirect to the last valid page
                const redirectParams = new URLSearchParams(searchParams);
                if (maxPage > 1) {
                    redirectParams.set('page', maxPage.toString());
                } else {
                    redirectParams.delete('page');
                }
                throw redirect(`${url.pathname}?${redirectParams.toString()}`);
            }
        }

        let ads: Awaited<ReturnType<typeof adRequest>> | null = null;
        let promotedAds: Awaited<ReturnType<typeof adRequest>> | null = null;
        let amplifiedItems: Awaited<
            ReturnType<typeof getItemToAmplify>
        > | null = null;

        if (!isLandingMode) {
            // Await the browse-only promises (already started if shouldSkipLandingData was true)
            // or start them now (for leaf categories where shouldSkipLandingData was false).
            // TODO: Pass AbortSignal to adRequest/getItemToAmplify once createApi supports it,
            // so these calls cancel when the user navigates away mid-load.
            [ads, promotedAds, amplifiedItems] = await raceAbort(
                signal,
                Promise.all([
                    browseAdsPromise ??
                        adRequest({
                            type: 'CategoryPlacement',
                            requestUrl: request.url,
                            addPathContext: ['region'],
                            ...context,
                            request,
                        }),
                    browsePromotedAdsPromise ??
                        adRequest({
                            type: 'PromotedDirectoryPlacement',
                            requestUrl: request.url,
                            addPathContext: ['region'],
                            ...context,
                            request,
                        }),
                    browseAmplifiedItemsPromise ??
                        getItemToAmplify({
                            context,
                            link: basePath,
                            limit: isDesktop ? 5 : 4,
                        }),
                ]),
            );
        }

        // Legacy filter states (keep for backward compatibility)
        const filterStates = aggregations
            ? createFilterStates({
                  aggregations,
                  appliedFilters: queryParams.filters,
                  aggregationsParams: aggregationProps.aggregations,
                  searchType: 'product',
                  visibilityType: 'directory',
                  categoryLink: basePath,
                  rootText: 'All',
                  sortBy: urlSort,
                  facetFilterFeatureEnabled,
                  hasMarketingView,
                  hiddenFacetIds: hiddenFacets?.map((facet) => facet.ID),
              })
            : createEmptyFilterStates({ visibilityType: 'directory' });

        // In landing mode, use rocco totalCount so header shows correct count instead of 0
        if (!aggregations && roccoAggregations?.totalCount != null) {
            filterStates.totalCount = roccoAggregations.totalCount;
        }

        const searchIslandPlaceholders = mapCategoryCountToPlaceholder(
            activeDirectoryCategoriesCount?.products ?? [],
            `Search products`,
            'products',
        );

        const appliedFilterCount = getAppliedFilterNumber(filterStates, [
            'searchLocation',
        ]);

        let productTilesRocco: TileViewModel<ProductTileViewModelRocco>[] = [];
        let professionalTilesRocco: TileViewModel<ProfessionalTileViewModel>[] =
            [];
        let adsShuffled: AdvertCreative[] = [];
        let promotedAdTiles: ArticleTileViewModel[] = [];
        let enableAwardBadges = false;

        // Eager-load first tiles when nothing pushes them down the viewport: filters applied,
        // no category or promoted placement ads, or suppliers view (ads are fetched but not shown
        // in the grid).
        const shouldPreloadImages =
            appliedFilterCount > 0 ||
            isSuppliersEndpoint ||
            (!ads?.length && !promotedAds?.length);
        const numberTilesToEagerLoad = isDesktop ? 4 : 2;

        // Skip tile processing in landing mode (searchResult is null)
        if (!isLandingMode) {
            if (isSuppliersEndpoint) {
                const professionals =
                    getProfessionalsFromESSearchResults(searchResult);
                professionalTilesRocco = professionals.map(
                    (professional, index) => ({
                        aspectRatio: 'default',
                        model: professional,
                        // Set eager loading for first N tiles to match preloading
                        eager:
                            shouldPreloadImages &&
                            index < numberTilesToEagerLoad,
                    }),
                );
            } else {
                const products = getProductsFromESSearchResults(
                    searchResult,
                    null,
                );
                productTilesRocco = products.map((product, index) => ({
                    aspectRatio: 'square',
                    model: product,
                    // Set eager loading for first N tiles to match preloading
                    eager:
                        shouldPreloadImages && index < numberTilesToEagerLoad,
                }));

                adsShuffled = ads ? shuffleArray(ads) : [];

                promotedAdTiles = promotedAds
                    ? (shuffleArray(promotedAds)?.reduce((acc, ad) => {
                          const tile = getArticleTileViewModelFromAd(ad);
                          if (tile) {
                              acc.push(tile);
                          }
                          return acc;
                      }, [] as ArticleTileViewModel[]) ?? [])
                    : [];

                enableAwardBadges = hasFeature(featureFlags, 'award_badges');
            }
        }

        return routerData(
            {
                isSuppliersEndpoint,
                appDisplayMode,
                directoryPageMeta,
                // Parse the footer FAQ on the server so SSR and client render the same
                // grid markup. The component's client fallback parser is DOM-only and
                // produces a different node tree, which breaks hydration of the document.
                parsedFooterContent: parseFooterContentToFAQ(
                    directoryPageMeta?.footerContent ?? '',
                ),
                searchResult,
                productTilesRocco,
                professionalTilesRocco,
                pagination,
                directoryCategoryCards,
                // Optimization: recentlyViewedProducts now fetched client-side via RecentlyViewedWrapper
                // If directory doesn't have a description, use the hardcoded fallback
                categoryDescription:
                    directoryPageMeta?.directoryHeaderDescription ||
                    `Find everything you need to build, whether it's a tap or a cladding system. Connect to purchase directly from ${fullCountryNameFromCode(context.localisation.country)}'s leading product suppliers.`,
                ads: adsShuffled,
                promotedAds: promotedAdTiles,
                localisation,
                filterStates: isSuppliersEndpoint
                    ? {
                          ...filterStates,
                          totalCount: searchResult?.nbHits ?? 0,
                      }
                    : filterStates,
                roccoFilterStates,
                categoryFiltersDropdownGroups,
                hasChildCategories,
                appliedFilterCount,
                enableAwardBadges,
                searchIslandPlaceholders,
                amplifiedItems,
                featuredPlacementResult,
                pageTheme: 'default' as const,
                // Deep-linking pagination data
                // firstLoadedPage: which page the loaded products start from (for skeleton rendering)
                // requestedPage: the page user navigated to (for scroll-to-section)
                firstLoadedPage,
                requestedPage,
                // Absolute offset the curated grid started at, so View More
                // continues past this page instead of re-fetching from zero and
                // repeating products already on screen. Zero when the curation
                // is off, which is the ordinary paging behaviour.
                defaultProductListOffset:
                    defaultProductListActive && !blockFallbackApplied
                        ? defaultProductListBlock * productsPerPage
                        : 0,
                // Size of the curated pool. The header count comes from the
                // unnarrowed aggregations, which would let View More keep
                // fetching past the end of the pinned suppliers' catalogue.
                defaultProductListTotal: defaultProductListActive
                    ? (searchResult?.nbHits ?? 0)
                    : null,
                productSection: (() => {
                    // Build URL→docCount map from ES aggregations to replace expensive
                    // GraphQL ItemsCount (which triggers per-category SQL COUNT queries)
                    const categoryCountMap = buildCategoryCountMap(
                        roccoAggregations?.categoryTree?.products,
                    );

                    return {
                        // Static data - previously from getHomeContent (4 unnecessary API calls)
                        prefixTitle: 'Shop & Specify',
                        suffixTitle: 'Products',
                        description: `No matter how big or small your building, renovation or redecoration project may be, you'll find plenty of high-quality products to choose from.`,
                        CTAButton: {
                            text: 'View all Products',
                            link: '/products',
                        },
                        categorySliderTitle: 'Browse by Product Category',
                        categories: applyCategoryFallbackImages(
                            mapCategoriesToTileViewModel(
                                productCategoriesResult?.Categories?.edges ??
                                    [],
                                categoryCountMap,
                            ),
                            fallbackImages,
                        ),
                        interiorCategories: applyCategoryFallbackImages(
                            mapCategoriesToTileViewModel(
                                (
                                    productCategoriesResult?.Categories
                                        ?.edges ?? []
                                ).filter(
                                    (edge) =>
                                        edge.node.CategoryGroup === 'Interior',
                                ),
                                categoryCountMap,
                            ),
                            fallbackImages,
                        ),
                        buildingCategories: applyCategoryFallbackImages(
                            mapCategoriesToTileViewModel(
                                (
                                    productCategoriesResult?.Categories
                                        ?.edges ?? []
                                ).find(
                                    (edge) =>
                                        edge.node.CategoryGroup === 'Building',
                                )?.node.Categories?.edges ?? [],
                                categoryCountMap,
                            ),
                            fallbackImages,
                        ),
                        count:
                            activeDirectoryCategoriesCount?.products.find(
                                (category) => category.title === 'Products',
                            )?.count ?? 0,
                        // True-to-ORM root list for the revamp's below-grid
                        // explore section: every top-level category in ORM
                        // order, no CategoryGroup filtering or reordering
                        // (that presentation belongs to the legacy
                        // Interior/Building toggle).
                        rootCategories: mapCategoriesToTileViewModel(
                            rootProductCategoriesResult?.Categories?.edges ??
                                [],
                            categoryCountMap,
                        ),
                        // Root-level categories for ExploreAllCategories (always all top-level, not URL-specific)
                        rootInteriorCategories: mapCategoriesToTileViewModel(
                            (
                                rootProductCategoriesResult?.Categories
                                    ?.edges ?? []
                            ).filter(
                                (edge) =>
                                    edge.node.CategoryGroup === 'Interior' ||
                                    edge.node.CategoryGroup === 'Both',
                            ),
                            categoryCountMap,
                        ),
                        rootBuildingCategories: mapCategoriesToTileViewModel(
                            (
                                rootProductCategoriesResult?.Categories
                                    ?.edges ?? []
                            ).filter(
                                (edge) =>
                                    edge.node.CategoryGroup === 'Building' ||
                                    edge.node.CategoryGroup === 'Both',
                            ),
                            categoryCountMap,
                        ),
                    };
                })(),
            },
            {
                headers: await session.getHeaders(),
            },
        );
    } catch (error) {
        if (isAbortError(error)) {
            throw new Response(null, { status: 499 });
        }
        throw error;
    }
};

type ProductsDirectoryData = Awaited<
    ReturnType<typeof productsDirectoryLoader>
>;

export const loader = async (args: LoaderFunctionArgs) => {
    const { featureFlags, localisation } = args.context;

    // Markets where the products section has not yet launched serve the
    // "Coming Soon" landing page and redirect every /products/* sub-path back
    // to /products. NZ/AU pass through via `countryConfig.allowProducts`; US
    // stays gated until the `us_product_launch` flag is enabled.
    //
    // The marker is cast to the directory data type to keep `typeof loader`
    // — and every downstream `useLoaderData<typeof loader>()` — a single,
    // stable shape.
    const productsLaunched = isUsProductLaunchEnabled(
        localisation.countryConfig,
        featureFlags,
    );
    if (!productsLaunched) {
        const { pathname } = new URL(args.request.url);
        if (pathname.replace(/\/+$/, '') !== '/products') {
            throw redirect('/products', 302);
        }
        return { usComingSoon: true } as unknown as ProductsDirectoryData;
    }

    // The carousel browse revamp always uses browse mode (never the landing
    // page): productsDirectoryLoader forces it off via isCategoryRevampActive
    // and adds the browse-page extras (category cards).
    return productsDirectoryLoader(args);
};

const ProductsCategoryPage = () => {
    const appDisplayMode = useAppDisplayModeContext();
    const { setCategory } = useCurrentCategory();
    const { desktop } = appDisplayMode;
    const {
        isSuppliersEndpoint,
        categoryDescription,
        directoryPageMeta,
        filterStates,
        roccoFilterStates,
        hasChildCategories,
        productTilesRocco,
        professionalTilesRocco,
        featuredPlacementResult,
        productSection,
        parsedFooterContent,
    } = useLoaderData<typeof loader>();

    // Merge Interior + Building categories for ExploreAllCategories (desktop only)
    const exploreCategories = useMemo(() => {
        const interior = productSection.rootInteriorCategories ?? [];
        const building = productSection.rootBuildingCategories ?? [];
        const seen = new Set(interior.map((cat) => cat.id));
        return [...interior, ...building.filter((cat) => !seen.has(cat.id))];
    }, [
        productSection.rootInteriorCategories,
        productSection.rootBuildingCategories,
    ]);

    const tracker = useTracker();

    // Initialize product page tracking
    useProductPageTracking();

    // In browse/grid mode (not landing pages), prevent browser's automatic
    // scroll restoration on POP. Must run synchronously during render because
    // Remix's <ScrollRestoration /> cleanup sets 'auto' before useEffect runs.
    // Landing pages (hasChildCategories && !browse && !suppliers) are left alone
    // — React Router's default scroll restoration works fine for them.
    const isBrowseMode =
        typeof window !== 'undefined' &&
        (!hasChildCategories ||
            isSuppliersEndpoint ||
            new URLSearchParams(window.location.search).has('browse'));
    if (isBrowseMode) {
        window.history.scrollRestoration = 'manual';
    }

    const [localIsSuppliers, setLocalIsSuppliers] =
        useState(isSuppliersEndpoint);
    const [disableHighlightTransition, setDisableHighlightTransition] =
        useState(false);

    // Track pathname and search params to determine if we should show featured content
    // Show featured content for non-leaf categories (categories with children)
    const location = useLocation();
    const navigationType = useNavigationType();
    const [searchParams] = useSearchParams();
    const fetchers = useFetchers();
    const allFetchersIdle = fetchers.every((f) => f.state === 'idle');

    // Tracks whether scroll restoration has completed after a POP navigation.
    // true on mount (no restoration needed), set false when POP restore starts,
    // set true after scrollTo completes. The scroll-save cleanup checks this
    // to avoid overwriting a saved position while restoration is in-flight.
    const scrollRestoredRef = useRef(true);

    // Track the current URL (including ?page=N from replaceState) for scroll save.
    // Updated during render (not in a layout effect) so the value is captured
    // before React processes effect cleanups. Only updates when window.location
    // still matches this route's pathname — React Router calls pushState before
    // unmount, so window.location may already reflect the NEW route during the
    // pre-unmount re-render. The guard prevents overwriting the ref with the
    // departing URL, ensuring scroll-save cleanup saves under the correct key.
    // Still captures ?page=N from replaceState since those only change search params.
    const scrollUrlRef = useRef('');
    if (
        typeof window !== 'undefined' &&
        window.location.pathname === location.pathname
    ) {
        scrollUrlRef.current =
            window.location.pathname + window.location.search;
    }

    // Track the latest scroll position via a passive scroll listener.
    // We read from this ref (not window.scrollY) when saving, because by the
    // time layout effect cleanup fires, React has already committed the new
    // render's DOM — which may be much shorter (e.g. showFeaturedContent
    // flipping from true→false removes thousands of pixels). The browser clamps
    // window.scrollY to the new max, losing the real position. The ref captures
    // the last known position from BEFORE any DOM changes.
    const lastScrollYRef = useRef(0);
    useEffect(() => {
        const onScroll = () => {
            lastScrollYRef.current = window.scrollY;
        };
        // Initialize with current position
        lastScrollYRef.current = window.scrollY;
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Track which product tile the user clicked (if any).
    // Uses [data-product-tile] to only match actual grid tiles, not
    // breadcrumbs, category links, or featured content links.
    const clickedTileLinkRef = useRef<string | null>(null);
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const tile = target.closest<HTMLAnchorElement>(
                '[data-product-tile]',
            );
            if (tile) {
                clickedTileLinkRef.current = tile.getAttribute('href');
            }
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    // Save scroll position + tile link when navigating away from a page.
    // The cleanup function captures location.pathname and location.search via
    // JavaScript closure from the render where it was created. When cleanup runs
    // during the next navigation (location.key changes), these captured values
    // correctly represent the OLD URL — the page the user is leaving.
    useIsomorphicLayoutEffect(() => {
        return () => {
            if (typeof window === 'undefined') return;
            // Don't save while scroll restoration is still in-flight —
            // the current scrollY may be 0 or an intermediate value that
            // would overwrite the correct saved position.
            if (!scrollRestoredRef.current) return;
            // Cap at 50 entries to prevent unbounded growth in long sessions
            if (scrollPositions.size >= 50) {
                const oldest = scrollPositions.keys().next().value;
                if (oldest !== undefined) scrollPositions.delete(oldest);
            }
            // scrollUrlRef captures ?page=N from replaceState (no re-render).
            // Only use it if its pathname matches the closure-captured location —
            // during navigation renders, scrollUrlRef may already hold the destination URL.
            const refPathname = scrollUrlRef.current?.split('?')[0];
            const url =
                scrollUrlRef.current && refPathname === location.pathname
                    ? scrollUrlRef.current
                    : location.pathname + location.search;
            const scrollY = lastScrollYRef.current;

            // Save clicked tile link, or fall back to first visible tile.
            // findFirstVisibleGridTile() only matches [data-product-tile]
            // elements, so it returns null on landing pages (no grid tiles)
            // — correctly falling back to scrollY-only restoration.
            const tileLink =
                clickedTileLinkRef.current ?? findFirstVisibleGridTile();
            clickedTileLinkRef.current = null;

            scrollPositions.set(url, { scrollY, tileLink });

            // Only in browse/grid mode (tileLink !== null): clear this route's
            // position from React Router's sessionStorage to prevent its
            // internal scroll restoration from firing on POP. On landing pages
            // (tileLink === null), leave React Router's restoration intact.
            if (tileLink) {
                try {
                    const rrPositions = JSON.parse(
                        sessionStorage.getItem('positions') || '{}',
                    );
                    const historyKey = window.history.state?.key;
                    if (historyKey && rrPositions[historyKey] !== undefined) {
                        delete rrPositions[historyKey];
                        sessionStorage.setItem(
                            'positions',
                            JSON.stringify(rrPositions),
                        );
                    }
                } catch {
                    // Ignore sessionStorage errors
                }
            }
        };
    }, [location.key, location.pathname, location.search]);

    // Track previous pathname to distinguish category navigation from filter changes
    const prevPathnameRef = useRef(location.pathname);
    // Track previous browse param to detect landing→browse transition
    const prevHadBrowseRef = useRef(searchParams.has('browse'));
    // Track previous page param to detect mobile pagination navigation
    const prevPageParamRef = useRef(searchParams.get('page'));
    // Ref mirror of allFetchersIdle — lets the navigation effect read the current value
    // without declaring it as a dependency (which would cause toggle re-fires).
    const allFetchersIdleRef = useRef(allFetchersIdle);
    allFetchersIdleRef.current = allFetchersIdle;
    // When scroll restore is deferred (fetchers not yet idle), stores the target URL.
    // Cleared once the deferred restore executes or a non-POP navigation occurs.
    const pendingScrollRestoreRef = useRef<string | null>(null);
    // Detect initial mount so we can scroll to top on PUSH navigation from
    // another page. prevPathnameRef is initialised with the current pathname,
    // so the "pathnameChanged" check below is always false on mount — this ref
    // fills that gap.
    const isInitialMountRef = useRef(true);

    // Scroll management: instant scroll to top on category nav, restore on back.
    // Restore is deferred until all fetchers are idle so the DOM is at full height
    // (prevents scroll clamping for sections near the bottom like ExploreAllCategories).
    // allFetchersIdle is intentionally read via ref — NOT in the dependency array.
    // Including it would cause toggle-triggered fetcher changes to re-fire this effect
    // while navigationType is still 'POP', incorrectly re-restoring scroll.
    useEffect(() => {
        // Don't interfere with hash navigation
        if (location.hash) return;

        // Browser back/forward (POP) or UI back button (restoreScroll): restore saved position
        const state = location.state as {
            restoreScroll?: boolean;
        } | null;
        const shouldRestore = navigationType === 'POP' || state?.restoreScroll;

        let outerRafId: number | undefined;
        let innerRafId: number | undefined;
        let cancelPoll: (() => void) | undefined;

        if (shouldRestore) {
            // Keep prevHadBrowseRef in sync even on POP navigations so the
            // landing→browse scroll-to-top check doesn't fire incorrectly
            // after the user navigates back to a ?browse=true page.
            prevHadBrowseRef.current = searchParams.has('browse');

            // Mark scroll as not yet restored — prevents the scroll-save
            // cleanup from overwriting the saved position prematurely.
            scrollRestoredRef.current = false;

            // Use window.location to match the save key (captures ?page=N from replaceState)
            const url =
                typeof window !== 'undefined'
                    ? window.location.pathname + window.location.search
                    : location.pathname + location.search;
            const saved = scrollPositions.get(url);

            if (saved !== undefined) {
                if (!allFetchersIdleRef.current) {
                    // Defer: deferred restore effect will execute when fetchers settle
                    pendingScrollRestoreRef.current = url;
                    prevPathnameRef.current = location.pathname;
                    return () => {
                        scrollRestoredRef.current = true;
                    };
                }

                pendingScrollRestoreRef.current = null;

                if (saved.tileLink) {
                    // Tile-aware restore: poll until the target tile appears
                    // in the DOM. The tile's presence signals that the grid
                    // has rendered, then we use saved scrollY for exact position.
                    cancelPoll = pollForTileAndScroll(
                        saved.tileLink,
                        saved.scrollY,
                        () => {
                            scrollRestoredRef.current = true;
                        },
                    );
                } else {
                    // No tile link saved: use existing double-RAF scrollTo
                    outerRafId = requestAnimationFrame(() => {
                        innerRafId = requestAnimationFrame(() => {
                            window.scrollTo({
                                top: saved.scrollY,
                                behavior: 'instant',
                            });
                            scrollRestoredRef.current = true;
                        });
                    });
                }

                prevPathnameRef.current = location.pathname;
                return () => {
                    if (outerRafId !== undefined)
                        cancelAnimationFrame(outerRafId);
                    if (innerRafId !== undefined)
                        cancelAnimationFrame(innerRafId);
                    cancelPoll?.();
                    // Unblock scroll saves if navigating away mid-poll
                    scrollRestoredRef.current = true;
                };
            }
            // POP with no saved position: mark as restored so future saves work
            scrollRestoredRef.current = true;
            if (navigationType === 'POP') {
                prevPathnameRef.current = location.pathname;
                return;
            }

            prevPathnameRef.current = location.pathname;
            return;
        }

        // Non-POP navigation: clear any pending deferred restore
        pendingScrollRestoreRef.current = null;

        // First mount from another page (PUSH): prevPathnameRef is initialised
        // with the current pathname so pathnameChanged would be false. Scroll to
        // top explicitly and bail — subsequent navigations use the ref-based
        // comparison below.
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            window.scrollTo({ top: 0, behavior: 'instant' });
            prevHadBrowseRef.current = searchParams.has('browse');
            return;
        }

        // Only scroll to top when pathname changes (category navigation)
        // or when the page param changes on mobile (pagination navigation).
        // Filter/sort changes only modify search params and use preventScrollReset.
        const pathnameChanged = prevPathnameRef.current !== location.pathname;
        prevPathnameRef.current = location.pathname;

        // Mobile pagination: page param changes but pathname stays the same.
        // Desktop View More uses replaceState (not React Router navigation), so
        // location.search won't change — this only triggers for mobile pagination links.
        const currentPageParam = new URLSearchParams(location.search).get(
            'page',
        );
        const pageParamChanged =
            (!desktop || isSuppliersEndpoint) &&
            currentPageParam !== prevPageParamRef.current;
        prevPageParamRef.current = currentPageParam;

        if (pathnameChanged || pageParamChanged) {
            window.scrollTo({ top: 0, behavior: 'instant' });
        } else if (searchParams.has('browse') && !prevHadBrowseRef.current) {
            // "View All Products" button adds ?browse=true on the same pathname.
            // Scroll to top only on landing→browse transition, not on filter/sort
            // changes that keep ?browse in the URL.
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
        prevHadBrowseRef.current = searchParams.has('browse');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        location.key,
        location.pathname,
        location.search,
        location.hash,
        location.state,
        navigationType,
        // allFetchersIdle intentionally excluded — read via allFetchersIdleRef above
    ]);

    // Execute a deferred scroll restore once all fetchers settle.
    // Toggle clicks only trigger this effect — since pendingScrollRestoreRef is null
    // after a completed restore, the effect short-circuits with no scroll.
    useEffect(() => {
        if (!allFetchersIdle) return;
        const url = pendingScrollRestoreRef.current;
        if (!url) return;
        pendingScrollRestoreRef.current = null;
        const saved = scrollPositions.get(url);
        if (saved !== undefined) {
            let cancel: (() => void) | undefined;
            if (saved.tileLink) {
                cancel = pollForTileAndScroll(
                    saved.tileLink,
                    saved.scrollY,
                    () => {
                        scrollRestoredRef.current = true;
                    },
                );
            } else {
                const outerRaf = requestAnimationFrame(() => {
                    const innerRaf = requestAnimationFrame(() => {
                        window.scrollTo({
                            top: saved.scrollY,
                            behavior: 'instant',
                        });
                        scrollRestoredRef.current = true;
                    });
                    cancel = () => cancelAnimationFrame(innerRaf);
                });
                cancel = () => cancelAnimationFrame(outerRaf);
            }
            return () => {
                cancel?.();
                // Unblock scroll saves if navigating away mid-poll
                scrollRestoredRef.current = true;
            };
        } else {
            // Saved entry was evicted (50-entry cap) — unblock scroll saves
            scrollRestoredRef.current = true;
        }
    }, [allFetchersIdle]);

    // Show featured content for non-leaf categories (desktop only).
    // Uses useMemo so the value is synchronously correct on every render.
    // Previously useState+useEffect caused a one-render delay where the stale
    // value from the prior page produced the wrong DOM layout, breaking scroll
    // restoration for sections near the bottom (e.g. ExploreAllCategories).
    const showFeaturedContent = useMemo(
        () =>
            desktop &&
            hasChildCategories &&
            !searchParams.has('browse') &&
            !isSuppliersEndpoint,
        [desktop, hasChildCategories, searchParams, isSuppliersEndpoint],
    );

    // Shared breadcrumb transformation — used by both TitleSectionDesktop and ProductsFilterBar.
    // For suppliers: rewrite hrefs to supplier URLs. For products: rename "Products" → "Product library".
    const transformedBreadcrumbs = useMemo(
        () =>
            isSuppliersEndpoint
                ? directoryPageMeta.breadcrumbs.map(({ href, label }) => ({
                      href: convertToProductSuppliersUrl(href),
                      label,
                  }))
                : directoryPageMeta.breadcrumbs.map(({ href, label }) => ({
                      href,
                      label: label === 'Products' ? 'Product library' : label,
                  })),
        [isSuppliersEndpoint, directoryPageMeta.breadcrumbs],
    );
    useEffect(() => {
        setLocalIsSuppliers(isSuppliersEndpoint);
    }, [isSuppliersEndpoint]);

    const navigate = useNavigate();

    const selectedCategory = getDirectoryFullName(directoryPageMeta);
    const selectedCompanies = searchParams.getAll('professionals').join(' | ');

    useEffect(() => {
        tracker.log('productListImpression', {
            label: 'Dentsu',
            url: new URL(window.location.href),
            data: {
                category: selectedCategory,
                brand: selectedCompanies,
            },
        });
    }, [selectedCategory, selectedCompanies, tracker]);

    useEffect(() => {
        setCategory('Products');
        return () => setCategory(null);
    }, [setCategory]);

    const { libraryActionValue } = useLibraryRemovalUi();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { filterDrawerOpen, toggleFilterDrawer: _toggleFilterDrawer } =
        useFilterDrawerPreference();

    // Grid columns: on products page, always 5 columns on desktop (no side panel)
    // On suppliers page, 4 columns when drawer is open, 5 when closed
    const gridColumns = desktop ? 4 : 2;

    const searchFilters = useSearchFilters({
        category: 'product',
        initialStates: filterStates,
        isCategoryPage: true,
        ignoreFilterKeys: CATEGORY_IGNORE_FILTER_KEYS,
    });

    // Rocco filter sort hook for pill-shaped filters
    // For suppliers page, filter out barFilters to hide Suppliers, Pricing, Specify, Availability dropdowns
    // Memoize config to prevent unnecessary re-renders
    const filteredConfigForSuppliers = useMemo(
        () => ({
            ...PRODUCT_FILTERS_CONFIG,
            barFilters: PRODUCT_FILTERS_CONFIG.barFilters.filter(
                (section) => !section.hideOnSuppliers,
            ),
        }),
        [],
    );

    const filterSortProps = useCategoryFilterSort(
        isSuppliersEndpoint
            ? filteredConfigForSuppliers
            : PRODUCT_FILTERS_CONFIG,
        SearchIndexes.Products,
        roccoFilterStates,
    );

    const searchFiltersSuppliers = useMemo(() => {
        return isSuppliersEndpoint
            ? {
                  ...searchFilters,
                  filterBarFilters: [],
                  filterStates: {
                      queryFilters: searchFilters.filterStates.queryFilters,
                      visibilityType: searchFilters.filterStates.visibilityType,
                      sortBy: searchFilters.filterStates.sortBy,
                      totalCount: searchFilters.filterStates.totalCount,
                      category: searchFilters.filterStates?.category,
                  },
              }
            : searchFilters;
    }, [isSuppliersEndpoint, searchFilters]);

    const handleSuppliersClick = useCallback(() => {
        // Reset all filters when switching between suppliers and products.
        // Add ?browse=true to keep users in directory mode when toggling
        navigate(
            buildToggleUrl(
                location.pathname,
                location.search,
                !isSuppliersEndpoint,
            ),
        );
    }, [isSuppliersEndpoint, location.pathname, location.search, navigate]);

    // Clear all filters by navigating to base URL
    const handleClearAllFilters = useCallback(() => {
        navigate(location.pathname);
    }, [navigate, location.pathname]);

    const hasProducts = filterStates.totalCount > 0;

    // Determine which grid to show based on data availability
    const shouldShowSupplierGrid =
        isSuppliersEndpoint && professionalTilesRocco.length > 0;
    const shouldShowProductGrid =
        !isSuppliersEndpoint && productTilesRocco.length > 0;

    return (
        <LibraryActionProvider value={libraryActionValue}>
            <div className="products-page-custom-filters mx-auto w-full qhd:max-w-[1920px]">
                {desktop ? (
                    <>
                        <TitleSectionDesktop
                            category={directoryPageMeta.directoryHeaderTitle}
                            rootCategory="Products."
                            categoryDescription={categoryDescription}
                            itemsInCategory={filterStates.totalCount}
                            isRoot={directoryPageMeta.isRoot}
                        />
                        {showFeaturedContent && (
                            <>
                                <CategoryPageProducts />

                                <BrowseByBrand />

                                {/* NEW: Editor's Choice */}
                                <EditorsChoice />

                                {/* NEW: The Editors' Edit - Articles */}
                                <EditorsEdit />

                                {/* Featured Banner Section */}
                                <div className="mb-20 qhd:px-12 md:mb-35">
                                    <FeaturedBanner
                                        banner={featuredPlacementResult}
                                        section="LandingPage"
                                    />
                                </div>

                                {/* Desktop recently viewed — above Explore All Categories */}
                                <RecentlyViewedWrapper displayMode="desktop" />

                                {/* Explore All Categories */}
                                <ExploreAllCategories
                                    categories={exploreCategories}
                                    title="Explore all product categories"
                                />
                            </>
                        )}
                    </>
                ) : (
                    <TitleSectionMobile
                        title={directoryPageMeta.directoryHeaderTitle}
                        categoryDescription={categoryDescription}
                        subtitle={''}
                    >
                        {!showFeaturedContent && (
                            <TitleSectionFilterMobile
                                esCategory="product"
                                searchFilters={searchFiltersSuppliers}
                                isSuppliersPage={isSuppliersEndpoint}
                                quickFiltersBefore={
                                    hasProducts
                                        ? [
                                              {
                                                  key: `suppliers-tab`,
                                                  iconBefore:
                                                      isSuppliersEndpoint ? (
                                                          <CheckboxFill />
                                                      ) : (
                                                          <CheckboxBlankLine />
                                                      ),
                                                  children: 'Suppliers',
                                                  onClick: handleSuppliersClick,
                                                  selected: false,
                                                  transparent: true,
                                                  outlined: true,
                                                  disabled: false,
                                                  className: '',
                                              },
                                          ]
                                        : []
                                }
                            />
                        )}
                    </TitleSectionMobile>
                )}

                {!showFeaturedContent &&
                    desktop &&
                    filterSortProps?.searchFilters && (
                        <ProductsFilterBar
                            breadcrumbs={transformedBreadcrumbs}
                            searchFilters={filterSortProps.searchFilters}
                            sortOptions={filterSortProps.sortOptions}
                            isSuppliersEndpoint={isSuppliersEndpoint}
                            onToggle={handleSuppliersClick}
                            localIsSuppliers={localIsSuppliers}
                            disableHighlightTransition={
                                disableHighlightTransition
                            }
                            setLocalIsSuppliers={setLocalIsSuppliers}
                            setDisableHighlightTransition={
                                setDisableHighlightTransition
                            }
                            supplierCategories={(
                                productSection.categories ?? []
                            ).map((cat) => ({
                                id: cat.id,
                                title: cat.title,
                                link: convertToProductSuppliersUrl(cat.link),
                            }))}
                        />
                    )}

                {!showFeaturedContent && (
                    <div className="w-full px-2.5 md:px-12.5">
                        {shouldShowSupplierGrid ? (
                            <SupplierGrid />
                        ) : shouldShowProductGrid ? (
                            <ProductGrid
                                gridColumns={gridColumns}
                                filterDrawerOpen={filterDrawerOpen}
                                isPurchasableChecked={
                                    searchFiltersSuppliers.filterStates
                                        .isPurchasable?.checked ?? false
                                }
                            />
                        ) : (
                            <NoResultsSection
                                resultsType={
                                    isSuppliersEndpoint
                                        ? 'suppliers'
                                        : 'products'
                                }
                                onClearFilters={handleClearAllFilters}
                            />
                        )}
                    </div>
                )}

                {/* Desktop browse mode: recently viewed above FAQ */}
                {!showFeaturedContent && desktop && (
                    <div className="mt-35">
                        <RecentlyViewedWrapper displayMode="desktop" />
                    </div>
                )}

                <div className="my-10 flex flex-col gap-10 md:mt-17.5 md:mb-20 md:gap-17.5">
                    {/* Mobile only — desktop handled above (landing: inside featured block, browse: above) */}
                    {!desktop && <RecentlyViewedWrapper displayMode="mobile" />}
                    <CategoryPageFAQ
                        faqSchemas={directoryPageMeta.faqSchemas}
                        footerContent={directoryPageMeta.footerContent}
                        parsedFooterContent={parsedFooterContent}
                        variant={desktop ? 'grid' : undefined}
                        categoryLabel={
                            desktop ? 'ArchiPro Products' : undefined
                        }
                    />
                </div>

                <BackToTopButton />
            </div>
        </LibraryActionProvider>
    );
};

// Route entry: render the standard products directory for markets where the
// products section has launched (NZ/AU via `allowProducts`, US once the
// `us_product_launch` flag is enabled); otherwise render the "Coming Soon"
// landing page. Mirrors the loader's gate so both agree on which shape
// `useLoaderData` returns.
/**
 * Products carousel browse page (behind the category_pages_revamp flag). Reuses
 * the legacy browse-mode grid (ProductGrid → DirectoryProductGrid) rendered with
 * portrait tiles, wrapped in the shared browse sections (intro + Get Matched,
 * category carousel, shared pill bar).
 */
const ProductsDirectoryBrowse = () => {
    const data = useLoaderData<typeof loader>();
    const { desktop } = useAppDisplayModeContext();
    const navigate = useNavigate();
    const browseLocation = useLocation();
    const isSuppliersEndpoint = data.isSuppliersEndpoint;

    // Admin Remove-From-Library UX (toast + undo + two-phase fade), shared
    // with the legacy directory via the same context the grid tiles read.
    const { libraryActionValue } = useLibraryRemovalUi();

    // Products and suppliers have different filter sets, so switching drops the
    // query entirely rather than carrying it across. No `browse=true`: the
    // revamp never renders the category landing view, so the flag has nothing
    // to suppress, and it would read as a user-applied filter and switch the
    // curated default product list off.
    const goToBrowseMode = useCallback(
        (toSuppliers: boolean) => {
            // Product filters don't apply to brands, so they clear on the way
            // over — but the refinement is stashed and restored when the
            // reader toggles back (QA). sessionStorage failures (private
            // mode) just lose the restore, never the toggle.
            const stashKey = 'products-browse-filter-stash';
            if (toSuppliers) {
                const params = new URLSearchParams(browseLocation.search);
                params.delete('page');
                const stash = params.toString();
                try {
                    if (stash) {
                        window.sessionStorage.setItem(stashKey, stash);
                    } else {
                        window.sessionStorage.removeItem(stashKey);
                    }
                } catch {
                    // Best-effort only.
                }
                navigate(buildTogglePath(browseLocation.pathname, true));
                return;
            }
            let restored = '';
            try {
                restored = window.sessionStorage.getItem(stashKey) ?? '';
            } catch {
                // Best-effort only.
            }
            navigate(
                `${buildTogglePath(browseLocation.pathname, false)}${
                    restored ? `?${restored}` : ''
                }`,
            );
        },
        [browseLocation.pathname, browseLocation.search, navigate],
    );

    // Fixed order regardless of which is active, so the refine sheet's pills
    // don't reshuffle when the user switches.
    const browseModes = useMemo(
        () => [
            {
                label: 'Products',
                isActive: !isSuppliersEndpoint,
                onSelect: () => goToBrowseMode(false),
            },
            {
                // "Brands" is the reader-facing name for this view. The URL
                // keeps its `/suppliers` segment — those links are already out
                // in the world — so the path and the label differ on purpose.
                label: 'Brands',
                isActive: isSuppliersEndpoint,
                onSelect: () => goToBrowseMode(true),
            },
        ],
        [isSuppliersEndpoint, goToBrowseMode],
    );
    const filterSortProps = useCategoryFilterSort(
        PRODUCT_FILTERS_CONFIG_REVAMP,
        SearchIndexes.Products,
        data.roccoFilterStates,
    );
    // Display dropdown: user-selected desktop grid columns for this vertical.
    const { columns: displayColumns, setColumns: setDisplayColumns } =
        useDirectoryDisplayColumns('products', 4);

    // Below-grid "Explore all product categories": the ORM's root list as-is.
    // The Interior-first + Building merge belongs to the legacy
    // Interior/Building toggle presentation; the revamp shows Building as a
    // level-1 category in ORM order.
    const exploreCategories = data.productSection.rootCategories ?? [];
    // Live root categories feed the modal's product-category step in place of
    // its static fallback list.
    const getMatchedCategories = useMemo(
        () =>
            exploreCategories.map((category) => ({
                value: category.title,
                label: category.title,
            })),
        [exploreCategories],
    );
    // categoryDescription is CMS HTML — strip tags and decode entities
    // (&nbsp;, &amp;, …) so the intro heading reads as plain text, then collapse
    // the whitespace the stripped tags leave behind.
    const heading = decodeEntities(
        (data.categoryDescription ?? '').replace(/<[^>]*>/g, ' '),
    )
        .replace(/\s+/g, ' ')
        .trim();
    const categoryCards = data.directoryCategoryCards ?? [];
    // Section heading: "All Products" at the root, "All {Category} Products" when
    // a category is active. At the root `category` is "Products" itself, so gate
    // on breadcrumb depth (root has a single crumb).
    const activeCategory =
        (data.directoryPageMeta?.breadcrumbs?.length ?? 0) > 1
            ? (data.directoryPageMeta?.category ?? undefined)
            : undefined;
    // The heading and pills follow the active browse mode, so the Brands view
    // never labels itself as products. It has to match the switch's own label
    // exactly — heading, "All …" pill and search prompt all derive from this.
    const entityNoun = isSuppliersEndpoint ? 'Brands' : 'Products';
    const sectionTitle = activeCategory
        ? `All ${activeCategory} ${entityNoun}`
        : `All ${entityNoun}`;
    // Category pills = the child categories. Prepend the "All …" pill only at
    // the root; on a category page the active category is already shown by the
    // back button, so the pill would be redundant.
    const childCategoryPills = categoryCards.map((category) => ({
        id: category.id,
        title: category.title,
        // Category links are product paths, so on the Brands view they have to
        // be rewritten or a pill would silently drop the user back to products.
        link: isSuppliersEndpoint
            ? buildTogglePath(category.link, true)
            : category.link,
        // Sidebar-only: gates the drill-in chevron; the pill bar ignores it.
        hasChildren: category.subCategories.length > 0,
    }));
    const categoryPills = activeCategory
        ? childCategoryPills
        : [
              {
                  id: 0,
                  title: `All ${entityNoun}`,
                  link: isSuppliersEndpoint
                      ? '/products/suppliers'
                      : '/products',
              },
              ...childCategoryPills,
          ];
    // The carousel cards carry the same product links as the pills, so on the
    // Brands view a card would drop the user back into products.
    // Breadcrumbs drive the back button, and they are product paths, so on the
    // Brands view they need the same rewrite as the pills — otherwise stepping
    // up a level silently drops the user back into products.
    const breadcrumbs = useMemo(() => {
        const crumbs = data.directoryPageMeta?.breadcrumbs ?? [];
        if (!isSuppliersEndpoint) {
            return crumbs;
        }

        return crumbs.map((crumb) => ({
            ...crumb,
            href: buildTogglePath(crumb.href, true),
        }));
    }, [data.directoryPageMeta?.breadcrumbs, isSuppliersEndpoint]);
    return (
        <LibraryActionProvider value={libraryActionValue}>
            {/* Listener for the intro / grid-injection Get Matched buttons
                (shared jotai atom). Ships with the revamp flag that gates this
                whole component — no separate Get Matched flag. From /products
                the audience is known, so the type-selector step is skipped
                via defaultEnquiryType. */}
            <GetMatchedModal
                defaultEnquiryType="product"
                productCategories={getMatchedCategories}
            />
            <DirectoryBrowsePage
                entityType="products"
                // Both browse modes read the product index, so the watermark
                // has to follow the mode rather than the entity type — the
                // same noun the heading and pills use.
                verticalTitle={entityNoun}
                intro={
                    <DirectoryIntro
                        // Names the page, not the index: the lead variant
                        // feeds this to the mobile h1 (sr-only at md+), so in
                        // Brands mode the page announced itself as Products.
                        eyebrow={`${entityNoun} Library`}
                        heading={heading}
                        categoryPrefix={activeCategory}
                    />
                }
                categoryCarousel={
                    // The category thumbnail carousel is off for now, so the
                    // pills and grid sit straight under the description. The
                    // component and its data are left in place — restoring it
                    // is putting the DirectoryCategoryCarousel back here,
                    // gated on `!activeCategory && categoryCards.length > 0`
                    // as before (root only; a category page already lists its
                    // children as pills).
                    null
                }
                pillBar={
                    filterSortProps?.searchFilters ? (
                        <DirectoryPillBar
                            breadcrumbs={breadcrumbs}
                            searchFilters={filterSortProps.searchFilters}
                            sortOptions={filterSortProps.sortOptions}
                            prefetchUrl={filterSortProps.settledDestinationURL}
                            rootPath={
                                isSuppliersEndpoint
                                    ? '/products/suppliers'
                                    : '/products'
                            }
                            rootLabel={`All ${entityNoun}`}
                            sectionTitle={sectionTitle}
                            categoryPills={categoryPills}
                            entitySwitch={{ modes: browseModes }}
                            resultName={
                                isSuppliersEndpoint ? 'Brand' : undefined
                            }
                            displayColumns={displayColumns}
                            onDisplayColumnsChange={setDisplayColumns}
                        />
                    ) : null
                }
                sidebar={
                    // Product facets don't apply to brands, so the Brands view
                    // carries no filter UI at all — no sidebar, and (via
                    // hasSidebar) no Show Filters / Refine pills either. The
                    // product filters are stashed on switch and restored when
                    // the reader toggles back (see goToBrowseMode).
                    !isSuppliersEndpoint && filterSortProps?.searchFilters ? (
                        <DirectoryFilterSidebar
                            searchFilters={filterSortProps.searchFilters}
                            sortOptions={filterSortProps.sortOptions}
                            // Categories are deliberately not passed: the pill
                            // bar above the grid already lists them and carries
                            // the Back control, and repeating them here pushed
                            // the facets down the panel.
                        />
                    ) : null
                }
                rows={
                    // The suppliers endpoint returns professional tiles, not
                    // product tiles, so the product grid would render its empty
                    // state over a full result set.
                    isSuppliersEndpoint ? (
                        <RevampSupplierGrid displayColumns={displayColumns} />
                    ) : (
                        <ProductGrid
                            portrait
                            filterDrawerOpen={false}
                            gridColumns={4}
                            isPurchasableChecked={false}
                            displayColumns={displayColumns}
                        />
                    )
                }
                belowGrid={
                    // Legacy order: recently viewed → explore all categories →
                    // newspaper FAQ. Now reachable below the capped grid.
                    <>
                        {desktop && (
                            <RecentlyViewedWrapper
                                displayMode="desktop"
                                className="mt-35"
                            />
                        )}
                        <ExploreAllCategories
                            categories={exploreCategories}
                            title="Explore all product categories"
                        />
                        <div className="my-10 flex flex-col gap-10 md:mt-17.5 md:mb-20 md:gap-17.5">
                            {!desktop && (
                                <RecentlyViewedWrapper displayMode="mobile" />
                            )}
                            <CategoryPageFAQ
                                faqSchemas={data.directoryPageMeta.faqSchemas}
                                footerContent={
                                    data.directoryPageMeta.footerContent
                                }
                                parsedFooterContent={data.parsedFooterContent}
                                variant={desktop ? 'grid' : undefined}
                                categoryLabel={
                                    desktop ? 'ArchiPro Products' : undefined
                                }
                            />
                        </div>
                    </>
                }
            />
        </LibraryActionProvider>
    );
};

const ProductsRoute = () => {
    const { localisation } = useRootData();
    const featureFlags = useFeatureFlags();
    const categoryRevamp = useCategoryRevamp();
    const productsLaunched = isUsProductLaunchEnabled(
        localisation.countryConfig,
        featureFlags,
    );
    if (!productsLaunched) {
        return <USProductsComingSoonPage />;
    }
    if (categoryRevamp) {
        return <ProductsDirectoryBrowse />;
    }
    return <ProductsCategoryPage />;
};

/**
 * Category surfaces opt their tiles into the listing→detail view transitions
 * (BC-4786): everything on this route sits inside the scope, so grid tiles,
 * sliders and recently-viewed strips all morph/sheet, while the same tile
 * components rendered on detail pages or the homepage navigate plainly.
 */
const ProductsRouteWithTileTransitions = () => (
    <TileViewTransitionScope>
        <ProductsRoute />
    </TileViewTransitionScope>
);

export default ProductsRouteWithTileTransitions;

// ProductGrid component for products
interface ProductGridProps {
    filterDrawerOpen: boolean;
    gridColumns: 1 | 2 | 3 | 4 | 5;
    isPurchasableChecked: boolean;
    /** Render tiles in the 5.0 portrait aspect (carousel browse revamp). */
    portrait?: boolean;
    /** Desktop columns from the browse Display dropdown (portrait grid only). */
    displayColumns?: DirectoryGridColumns;
}

const ProductGrid = React.memo(
    ({
        filterDrawerOpen,
        gridColumns,
        isPurchasableChecked,
        portrait = false,
        displayColumns = 4,
    }: ProductGridProps) => {
        const gridRef = useRef<HTMLDivElement | null>(null);
        useCategoryTilesTracking(gridRef);

        // Full-width modules (Get Matched, sign-in) cycled into the browse grid.
        const directoryInjections = useDirectoryGridInjections();

        const {
            pagination,
            productTilesRocco: productTiles,
            directoryPageMeta,
            ads,
            promotedAds,
            appliedFilterCount,
            amplifiedItems: amplifiedItemsLoader,
            featuredPlacementResult,
            filterStates,
            firstLoadedPage,
            requestedPage,
            defaultProductListOffset,
            defaultProductListTotal,
        } = useLoaderData<typeof loader>();

        const [amplifiedItems, setAmplifiedItems] =
            useState(amplifiedItemsLoader);
        const remixLocation = useLocation();
        const { desktop, mobile } = useAppDisplayModeContext();
        const theme = useTheme();
        const { toggleFilterDrawer } = useFilterDrawerPreference();
        const { isInDesignBoard } = useHandleFollow();
        const { isSafari } = useDeviceBrowserContext();
        // Use View More hook for desktop pagination with skeleton lazy-loading.
        // Receives the raw loader output so the hook's initialProducts reference
        // stays stable across the lifetime of a category page — admin enrichment
        // is applied to the hook's output below.
        const {
            products: viewMoreProducts,
            hasMore,
            isLoading: isLoadingMore,
            error: loadMoreError,
            loadMore,
            loadSkeletonPage,
            loadingPages,
            currentFirstLoadedPage,
            isRestoredFromCache,
            nextPage,
        } = useProductViewMore({
            initialProducts:
                productTiles as TileViewModel<ProductTileViewModelRocco>[],
            // The curated grid is bounded by the pinned suppliers' catalogue,
            // not the full one the header reports.
            totalCount: defaultProductListTotal ?? filterStates.totalCount,
            categoryLink: remixLocation.pathname,
            filters: filterStates.queryFilters,
            // The loader reads the keyword term straight off the request URL,
            // so the first page is already narrowed; repeat it here or View
            // More would append from the unfiltered set.
            search:
                new URLSearchParams(remixLocation.search)
                    .get('search')
                    ?.trim() || undefined,
            sortBy: filterStates.sortBy ?? undefined,
            firstLoadedPage,
            // The 5.0 uniform grid pages in 36s (36-cell pages, amplifier
            // cells included); the legacy A/B/A grid keeps 42.
            pageSize: portrait ? BROWSE_PRODUCTS_PER_PAGE : PRODUCTS_PER_PAGE,
            // Non-zero when the curated products root entered the result set at
            // a random block, so View More continues from there.
            offsetBase: defaultProductListOffset,
            // The 5.0 uniform grid keeps `?page=N` in step with the page in
            // view (useViewportPageSync); the legacy grids keep the
            // request-time rewrite (button-only paging, so it's accurate).
            updateUrlOnLoad: !portrait,
        });

        // Crawlable `?page=N` anchor for the Load More control — mirrors the
        // deep-link URL a click loads, so bots can walk the pages without JS.
        const nextPageHref = useMemo(() => {
            const params = new URLSearchParams(remixLocation.search);
            params.set('page', String(nextPage));
            return `${remixLocation.pathname}?${params.toString()}`;
        }, [remixLocation.pathname, remixLocation.search, nextPage]);

        // Enrich the merged product set (initial + View More + skeleton pages)
        // with admin data. View More additions get adminStatusInfo too.
        // The generalized hook returns the base tile type; these are products.
        const productTilesRoccoAll = useTilesAdminData(
            viewMoreProducts as unknown as TileViewModel<ProductTileViewModelRocco>[],
        );

        // Admin Remove-From-Library wiring from the route-level provider:
        // `removeFromLibrary` feeds the tile context menu (undefined for
        // non-admins), `removedFromLibraryIds` drives the fade-out, and tiles
        // whose fade has finished are dropped so the grid reflows and fills
        // the empty cell.
        const {
            removeFromLibrary,
            removedFromLibraryIds,
            unmountedFromLibraryIds,
        } = useLibraryAction();
        const productTilesRocco = useMemo(() => {
            const visible =
                unmountedFromLibraryIds && unmountedFromLibraryIds.size > 0
                    ? productTilesRoccoAll.filter(
                          (t) => !unmountedFromLibraryIds.has(t.model.id),
                      )
                    : productTilesRoccoAll;
            return portrait
                ? visible.map((t) => ({
                      ...t,
                      // The taller product frame (354/521 vs portrait's
                      // 437/524): product shots are square on white, so the
                      // extra height reads as margin around the item rather
                      // than a tighter crop into it.
                      aspectRatio: 'productTall' as const,
                  }))
                : // Legacy grid is uniformly square. The generalized load-more
                  // action returns tiles without an aspect ratio, so re-stamp it
                  // here (View More additions would otherwise render at the
                  // default aspect).
                  visible.map((t) => ({
                      ...t,
                      aspectRatio: 'square' as NonNullable<
                          typeof t.aspectRatio
                      >,
                  }));
        }, [productTilesRoccoAll, unmountedFromLibraryIds, portrait]);

        // Keep the first value returned by loader to avoid changing when loader revalidate
        const adsMemoized = useLocationMemo(ads, ['region']);
        const promotedAdsMemoized = useLocationMemo(promotedAds, ['region']);
        const isAnalyticsAmplifierTilesOn = useABTestIsOn(
            'analytics-amplifier-tiles',
        );

        // Process product tiles
        const productBaseTiles = useMemo(() => {
            return productTilesRocco.map((tile) => {
                return (
                    <div
                        key={tile.model.id}
                        className="transform-gpu will-change-transform"
                        style={{
                            transition: 'transform 300ms ease',
                        }}
                    >
                        <CategoryPageTileWithAdminActions tile={tile} />
                    </div>
                );
            });
        }, [productTilesRocco]);

        // Process all tiles including ads and promoted content
        const processedTiles = useMemo(() => {
            const result = [...productBaseTiles];

            // Amplified tile positions: narrow layout on mobile, wide on desktop
            // (no side panel exists on desktop after BC-4227 cleanup).
            const tilePositions = mobile
                ? [7, 14, 21, 28]
                : [9, 18, 27, 36, 40];

            if (isAnalyticsAmplifierTilesOn) {
                if (amplifiedItems?.length) {
                    tilePositions.forEach((position, index) => {
                        const item = amplifiedItems[index];
                        if (position < result.length && item) {
                            const amplifiedTile = (
                                <div
                                    key={`amplified_${position}`}
                                    className="aspect-6/8 md:aspect-6/7"
                                >
                                    <AnalyticsAmplifierTile
                                        professionalId={item.professionalId}
                                        image={item.image}
                                        logo={item.logo}
                                        name={item.title}
                                        subtitle={item.aiServiceSummary}
                                        website={item.websiteLink}
                                        backgroundColor={item.backgroundColor}
                                    />
                                </div>
                            );
                            result.splice(position, 0, amplifiedTile);
                        }
                    });
                }
            }

            // Insert promoted ad tile if conditions are met
            if (
                desktop &&
                pagination.page === 1 &&
                result.length > 0 &&
                promotedAdsMemoized.length &&
                promotedAdsMemoized[0] &&
                promotedAdsMemoized[0].ad
            ) {
                const offset = 9;
                if (result.length >= offset) {
                    const adTile = promotedAdsMemoized[0];
                    const professionalID = adTile.professionalID ?? 0;
                    const imageProps = {
                        src: adTile.image,
                        width: '100%',
                        height: '100%',
                        sizes: getImagesSizes(310),
                        alt: adTile.title,
                    };
                    result.splice(
                        offset,
                        0,
                        <FollowSelectorProxy
                            key={`promoted_${adTile.ID}`}
                            pinId={professionalID}
                            pinType="ProfessionalPin"
                        >
                            {({ openFollowSelector }) => (
                                <ArchizeenAdvertTile
                                    tag={'ARTICLE'}
                                    advertCreative={adTile.ad!}
                                    prefetch="intent"
                                    image={imageProps}
                                    size="large"
                                    variant={'08'}
                                    variables={{
                                        color: theme.siteVariables.colors
                                            .charcoal['250'],
                                        dynamicSizingWidth: 310,
                                        dynamicSizingGapWidth: 0,
                                        tilePadding: pxToRem(10),
                                    }}
                                    onFavourite={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        openFollowSelector(e);
                                    }}
                                    isFavourited={isInDesignBoard(
                                        professionalID,
                                        'ProfessionalPin',
                                    )}
                                    title={adTile.title}
                                    label={{
                                        children: adTile.author,
                                        variant: '06',
                                    }}
                                    date={{
                                        children: adTile.date,
                                        variant: '06',
                                    }}
                                    readTime={{
                                        children: adTile.readTime,
                                        variant: '06',
                                    }}
                                />
                            )}
                        </FollowSelectorProxy>,
                    );
                }
            }

            // Add DetailedProductSpliter if needed
            const category =
                directoryPageMeta.category !== 'Products'
                    ? directoryPageMeta.category
                    : undefined;
            if (productTilesRocco && isPurchasableChecked) {
                const lastPurchasableItemIndex = findLastIndex(
                    productTilesRocco,
                    (t) => t.model.canPurchase,
                );
                const purchasableCount =
                    productTilesRocco?.filter((t) => t.model.canPurchase)
                        .length ?? 0;
                if (
                    purchasableCount === 0 ||
                    (lastPurchasableItemIndex !== undefined &&
                        lastPurchasableItemIndex !== -1)
                ) {
                    const rowIndex = Math.ceil(
                        (lastPurchasableItemIndex + 1) / gridColumns,
                    );
                    const insertionIndex = rowIndex * gridColumns;
                    if (insertionIndex < productTilesRocco.length) {
                        const moreProductsComing = (
                            <DetailedProductSpliter
                                key="MoreProductsComing"
                                category={category}
                                columns={gridColumns}
                            />
                        );
                        result.splice(insertionIndex, 0, moreProductsComing);
                    }
                }
            }

            // Add banner ad if no filters are applied
            if (
                appliedFilterCount === 0 &&
                adsMemoized.length &&
                adsMemoized[0]
            ) {
                result.unshift(
                    <AdvertBannerTile
                        key="AdvertBannerTile"
                        advertCreative={adsMemoized[0]}
                        columns={gridColumns}
                        desktop={desktop}
                    />,
                );
            }

            return result;
        }, [
            productBaseTiles,
            mobile,
            desktop,
            pagination.page,
            promotedAdsMemoized,
            theme,
            isInDesignBoard,
            directoryPageMeta.category,
            isPurchasableChecked,
            productTilesRocco,
            gridColumns,
            appliedFilterCount,
            adsMemoized,
            amplifiedItems,
            isAnalyticsAmplifierTilesOn,
        ]);

        useEffect(() => {
            // Randomise amplified items when URL changes, but not when forms are submitted.
            setAmplifiedItems(amplifiedItemsLoader);
        }, [amplifiedItemsLoader, remixLocation.key]);

        // Amplifier (AAA) tile nodes, shared by both grids. The 5.0 browse
        // grid shows them whenever items exist — they are part of its page
        // layout, not a per-visitor variation. The legacy grid keeps the AB
        // gate below.
        const amplifierTileNodes = useMemo(() => {
            if (!amplifiedItems?.length) {
                return undefined;
            }
            return amplifiedItems.map((item, index) => (
                <AnalyticsAmplifierTile
                    key={`amplifier_section_${index}`}
                    professionalId={item.professionalId}
                    image={item.image}
                    logo={item.logo}
                    name={item.title}
                    subtitle={item.aiServiceSummary}
                    website={item.websiteLink}
                    backgroundColor={item.backgroundColor}
                />
            ));
        }, [amplifiedItems]);
        // Legacy desktop grid (1 per section), still behind the AB test.
        const amplifierTiles = isAnalyticsAmplifierTilesOn
            ? amplifierTileNodes
            : undefined;

        if (productTilesRocco.length === 0) {
            return <NoResultsSection resultsType={'products'} />;
        }

        // Create ad tile for DirectoryProductGrid featured slot.
        // Uses PromotedDirectoryPlacement ad — the tall ad tile with company info.
        // Falls back to product image featured tile when no promoted ad is available.
        const promotedAdCreative =
            promotedAdsMemoized.length && promotedAdsMemoized[0]?.ad
                ? promotedAdsMemoized[0].ad
                : null;
        const promotedFeaturedAdTile = promotedAdCreative ? (
            <DirectoryAdTile
                key="ad_section_a"
                advertCreative={promotedAdCreative}
            />
        ) : null;

        // 5.0 browse: uniform portrait grid (no A/B featured sections) on every
        // breakpoint, keeping the View More hook's infinite scroll, deep-link
        // skeletons and POP cache. Matches the carousel-browse Figma design.
        if (portrait) {
            return (
                <DirectoryUniformGrid
                    productTiles={productTilesRocco}
                    // Match the tiles' own frame so the placeholders don't
                    // resize as real tiles replace them.
                    skeletonAspectClass="aspect-tile-product-tall"
                    // The Display selection renders exactly as chosen — no
                    // sidebar clamp; tiles just narrow while it's open.
                    columns={displayColumns}
                    pageSize={BROWSE_PRODUCTS_PER_PAGE}
                    totalCount={filterStates.totalCount}
                    isLoading={isLoadingMore}
                    hasMore={hasMore}
                    onLoadMore={loadMore}
                    hybridLoad
                    nextPageHref={nextPageHref}
                    // This instance owns ?page (updateUrlOnLoad is off on its
                    // view-more hook). The suppliers grid below still must NOT
                    // set this: its loader hydrates one page rather than the
                    // deep-link run, so viewport sync has nothing to count
                    // against and would report the wrong page.
                    syncPageParam
                    trackCategory={remixLocation.pathname}
                    error={loadMoreError}
                    firstLoadedPage={currentFirstLoadedPage}
                    requestedPage={requestedPage}
                    onLoadSkeletonPage={loadSkeletonPage}
                    loadingPages={loadingPages}
                    injections={directoryInjections}
                    amplifierTiles={amplifierTileNodes}
                    activeCategoryLabel={
                        (directoryPageMeta?.breadcrumbs?.length ?? 0) > 1
                            ? (directoryPageMeta?.category ?? undefined)
                            : undefined
                    }
                    removedFromLibraryIds={removedFromLibraryIds}
                    onRemoveFromLibrary={removeFromLibrary}
                />
            );
        }

        // Desktop: Use new DirectoryProductGrid layout with View More (no pagination fallback)
        if (desktop) {
            return (
                <DirectoryProductGrid
                    productTiles={productTilesRocco}
                    featuredBanner={featuredPlacementResult}
                    featuredAdTile={promotedFeaturedAdTile}
                    amplifierTiles={amplifierTiles}
                    isLoading={isLoadingMore}
                    hasMore={hasMore}
                    onLoadMore={loadMore}
                    error={loadMoreError}
                    hasActiveFilters={appliedFilterCount > 0}
                    totalCount={filterStates.totalCount}
                    firstLoadedPage={currentFirstLoadedPage}
                    requestedPage={requestedPage}
                    onLoadSkeletonPage={loadSkeletonPage}
                    loadingPages={loadingPages}
                    skipScrollToSection={isRestoredFromCache}
                    isRestoredFromCache={isRestoredFromCache}
                />
            );
        }

        // Mobile: Keep existing VirtualizedGrid layout
        return (
            <>
                <VirtualizedGrid
                    items={processedTiles}
                    renderItem={(item) => item}
                    startMarker={
                        <div className="hidden">
                            <CollapsibleToggle
                                filterDrawerOpen={filterDrawerOpen}
                                toggleFilterDrawer={toggleFilterDrawer}
                            />
                        </div>
                    }
                    endMarker={
                        pagination.totalPages > 1 && (
                            <div className="pt-26.25 md:pt-20">
                                <TilePagination
                                    currentPath={remixLocation}
                                    current={pagination.page}
                                    total={pagination.totalPages}
                                    urlBuilder={buildPaginationUrl}
                                    visibleCount={3}
                                />
                            </div>
                        )
                    }
                    className={cn(
                        'grid transition-all duration-300 ease-in-out',
                        'grid-cols-2',
                        'gap-x-2 gap-y-12',
                    )}
                    enabled={isSafari}
                    initialVisibleCount={15}
                    batchSize={10}
                    loadMoreThreshold={0.7}
                    reduceThreshold={0.3}
                    minVisibleCount={15}
                />
            </>
        );
    },
);

/**
 * Suppliers grid for the revamped browse page. Renders the same uniform grid
 * the professionals revamp uses, so brand tiles match across the two surfaces
 * and pick up the shared gutters and column behaviour, and pages with the same
 * View More control as every other browse grid — the load-more endpoint reads
 * the `/suppliers` segment off the category link and repeats the loader's
 * aggregate-then-search pass for the next page.
 */
const RevampSupplierGrid = React.memo(
    ({ displayColumns = 4 }: { displayColumns?: DirectoryGridColumns }) => {
        const {
            pagination,
            professionalTilesRocco: professionalTiles,
            filterStates,
        } = useLoaderData<typeof loader>();
        const remixLocation = useLocation();

        const {
            products: viewMoreTiles,
            hasMore,
            isLoading,
            error,
            loadMore,
        } = useDirectoryViewMore({
            initialProducts:
                professionalTiles as TileViewModel<TileableModel>[],
            totalCount: filterStates.totalCount,
            categoryLink: remixLocation.pathname,
            filters: filterStates.queryFilters,
            // Repeated on every later page, as on the products grid: the
            // loader narrowed the first one from the same term.
            search:
                new URLSearchParams(remixLocation.search)
                    .get('search')
                    ?.trim() || undefined,
            sortBy: filterStates.sortBy ?? undefined,
            entityType: 'products',
            pageSize: DEFAULT_SUPPLIERS_PAGE_SIZE,
            // The loader still honours a `?page=N` deep link (old links and
            // crawled pager URLs), serving that page rather than page 1. Tell
            // the hook where the server entered the set so View More continues
            // from there instead of re-fetching from zero.
            offsetBase: (pagination.page - 1) * DEFAULT_SUPPLIERS_PAGE_SIZE,
            // With the pager gone there is no page number to keep in the URL —
            // and rewriting it would fight `offsetBase` on a deep link.
            updateUrlOnLoad: false,
        });

        const withAdmin = useTilesAdminData(
            viewMoreTiles as TileViewModel<ProfessionalTileViewModel>[],
        );

        const tiles = useMemo(
            () =>
                withAdmin.map((tile) => ({
                    ...tile,
                    aspectRatio: 'portrait' as const,
                })),
            [withAdmin],
        );

        // Crawlable next-page target for the View More anchor. Built from the
        // page the server rendered rather than the hook's running count: it is
        // the pre-interaction SSR value a crawler sees, and the hook's own
        // `nextPage` does not account for `offsetBase` on a deep link.
        const nextPageHref = useMemo(() => {
            if (pagination.page >= pagination.totalPages) return undefined;
            const params = new URLSearchParams(remixLocation.search);
            params.set('page', String(pagination.page + 1));
            return `${remixLocation.pathname}?${params.toString()}`;
        }, [
            pagination.page,
            pagination.totalPages,
            remixLocation.pathname,
            remixLocation.search,
        ]);

        if (tiles.length === 0) {
            return <NoResultsSection resultsType={'suppliers'} />;
        }

        return (
            <DirectoryUniformGrid
                productTiles={tiles}
                // The Display selection renders exactly as chosen — no
                // sidebar clamp, matching the products grid.
                columns={displayColumns}
                // Two up on mobile like the products grid: brand tiles are
                // logo-and-name, so a single column wastes most of the row.
                mobileColumns={2}
                entityNoun="brands"
                pageSize={DEFAULT_SUPPLIERS_PAGE_SIZE}
                totalCount={filterStates.totalCount}
                hasMore={hasMore}
                isLoading={isLoading}
                onLoadMore={loadMore}
                // First chunk → View More → infinite scroll, matching every
                // other browse grid. Without this the sentinel is live from the
                // first paint and the button never gates anything.
                hybridLoad
                nextPageHref={nextPageHref}
                error={error}
            />
        );
    },
);

// SupplierGrid component for suppliers
const SupplierGrid = React.memo(() => {
    const { pagination, professionalTilesRocco: professionalTiles } =
        useLoaderData<typeof loader>();

    const remixLocation = useLocation();
    const appDisplayMode = useAppDisplayModeContext();
    const professionalTilesRocco = useTilesAdminData(
        professionalTiles as TileViewModel<ProfessionalTileViewModel>[],
    );

    // Process supplier tiles
    const supplierBaseTiles = useMemo(() => {
        return professionalTilesRocco.map((tile) => {
            return (
                <div
                    key={tile.model.id}
                    className="transform-gpu will-change-transform"
                    style={{
                        transition: 'transform 300ms ease',
                    }}
                >
                    <CategoryPageTileWithAdminActions tile={tile} />
                </div>
            );
        });
    }, [professionalTilesRocco]);

    if (supplierBaseTiles.length === 0) {
        return <NoResultsSection resultsType={'suppliers'} />;
    }

    return (
        <>
            <Grid
                colClasses="grid-cols-1 md:grid-cols-4"
                rowsGapClass="gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20"
                className="transition-all duration-300 ease-in-out"
            >
                {supplierBaseTiles}
            </Grid>

            {pagination.totalPages > 1 && (
                <div className="pt-26.25 md:pt-20">
                    <TilePagination
                        currentPath={remixLocation}
                        current={pagination.page}
                        total={pagination.totalPages}
                        urlBuilder={buildPaginationUrl}
                        visibleCount={appDisplayMode.desktop ? 4 : 3}
                    />
                </div>
            )}
        </>
    );
});
